import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import RichTextEditor from './components/RichTextEditor';
import {
  HomeIcon,
  UserGroupIcon,
  BriefcaseIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  BuildingOfficeIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentCheckIcon,
  StarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import './App.css';
import StrategicDiscoveryForm from './components/StrategicDiscoveryForm';
import ActivityLog from './pages/ActivityLog';
// import logo from './logo.svg';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Custom spyglass/telescope icon component for Voyager
const SpyglassIcon = ({ className = "h-6 w-6" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth={1.5} fill="none" />
    <line x1="3" y1="3" x2="8" y2="8" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
  </svg>
);

interface ProjectPhase {
  name: string;
  owner: string;
  duration: string;
  activities: string[];
  deliverables: string[];
  criteriaToProceed: string[];
}

// Project Type Templates
const PROJECT_TEMPLATES: { [key: string]: ProjectPhase[] } = {
  'Google Ad': [
    {
      name: 'Phase 1: Campaign Strategy',
      owner: 'Strategy Division',
      duration: '1 week',
      activities: [
        'Client goals and KPI definition',
        'Target audience research',
        'Competitive analysis',
        'Budget allocation planning',
        'Campaign structure planning'
      ],
      deliverables: [
        'Campaign Strategy Brief',
        'Target audience profiles',
        'Budget allocation plan',
        'KPI tracking framework'
      ],
      criteriaToProceed: [
        'Strategy Brief approved by client',
        'Budget confirmed',
        'KPIs defined and agreed upon',
        'Target audiences validated'
      ]
    },
    {
      name: 'Phase 2: Creative Development',
      owner: 'Production Division (Creative)',
      duration: '1-2 weeks',
      activities: [
        'Keyword Research, Definition and Signoff',
        'Ad copy creation (multiple variations)',
        'Visual asset design',
        'Landing page design (if needed)',
        'A/B testing variations',
        'Client review rounds'
      ],
      deliverables: [
        'Ad copy variations (minimum 3 per ad group)',
        'Visual assets (all sizes required)',
        'Landing page designs',
        'Creative approval documentation'
      ],
      criteriaToProceed: [
        'All ad copy approved',
        'Visual assets approved in all required sizes',
        'Landing pages approved',
        'Brand compliance verified'
      ]
    },
    {
      name: 'Phase 3: Campaign Setup',
      owner: 'Insights & Innovation Division',
      duration: '3-5 days',
      activities: [
        'Google Ads account setup',
        'Campaign structure implementation',
        'Ad group creation',
        'Keyword research and implementation',
        'Bid strategy configuration',
        'Conversion tracking setup',
        'Quality assurance testing'
      ],
      deliverables: [
        'Live Google Ads campaigns',
        'Tracking implementation',
        'Campaign setup documentation',
        'Access permissions configured'
      ],
      criteriaToProceed: [
        'All campaigns built and tested',
        'Tracking verified and working',
        'Client has been granted access',
        'Launch checklist completed',
        'Budget pacing verified'
      ]
    },
    {
      name: 'Phase 4: Monitoring & Optimization',
      owner: 'Insights & Innovation Division',
      duration: 'Ongoing',
      activities: [
        'Daily performance monitoring',
        'Bid adjustments',
        'A/B test analysis',
        'Negative keyword management',
        'Budget pacing adjustments',
        'Weekly reporting',
        'Monthly strategy reviews'
      ],
      deliverables: [
        'Weekly performance reports',
        'Monthly optimization recommendations',
        'Quarterly strategy reviews'
      ],
      criteriaToProceed: [
        '30-day performance review completed',
        'Optimization opportunities identified',
        'Client satisfied with performance',
        'Reporting cadence established',
        'Next Campaign Call Scheduled'
      ]
    }
  ],
  'Print Design': [
    {
      name: 'Phase 1: Project Brief & Strategy',
      owner: 'Account Manager',
      duration: '3-5 days',
      activities: [
        'Client brief and goals definition',
        'Audience and messaging workshop',
        'Competitive review',
        'Technical specifications gathering',
        'Budget and timeline confirmation'
      ],
      deliverables: [
        'Creative Brief',
        'Technical specifications document',
        'Project timeline',
        'Budget breakdown'
      ],
      criteriaToProceed: [
        'Creative Brief signed by client',
        'Technical specs confirmed (size, bleed, color mode, etc.)',
        'Budget approved',
        'Timeline agreed upon',
        'Internal Kickoff'
      ]
    },
    {
      name: 'Phase 2: Concept Development',
      owner: 'Production Division (Design)',
      duration: '1-2 weeks',
      activities: [
        'Concept ideation',
        'Initial design directions (2-3 options)',
        'Client presentation',
        'Revision round',
        'Concept selection'
      ],
      deliverables: [
        'Concept boards (2-3 options)',
        'Presentation deck',
        'Selected concept with rationale'
      ],
      criteriaToProceed: [
        'Concept selected by client',
        'Design direction approved by client',
        'Feedback incorporated',
        'Next phase scope confirmed'
      ]
    },
    {
      name: 'Phase 3: Design Execution',
      owner: 'Production Division (Design)',
      duration: '1-3 weeks',
      activities: [
        'Final design development',
        'Copywriting and content refinement',
        'Image sourcing/creation',
        'Design revisions (1-3 rounds)',
        'Final proofing'
      ],
      deliverables: [
        'Final print-ready design',
        'All source files',
        'Proof sheet with specifications',
        'Digital proof for client approval'
      ],
      criteriaToProceed: [
        'Design approved by client',
        'All copy proofread and approved',
        'Images approved and licensed',
        'Print specifications verified',
        'Client sign-off received'
      ]
    },
    {
      name: 'Phase 4: Production & Delivery',
      owner: 'Production Division',
      duration: '1-3 weeks (varies by printer)',
      activities: [
        'File preparation for print',
        'Printer coordination',
        'Proof review (if applicable)',
        'Print production monitoring',
        'Quality control check',
        'Delivery coordination'
      ],
      deliverables: [
        'Print-ready files delivered to printer',
        'Printer proof (if applicable)',
        'Final printed materials',
        'Project archive (all files)'
      ],
      criteriaToProceed: [
        'Print quality verified',
        'Quantity confirmed',
        'Delivery completed',
        'Client satisfaction confirmed',
        'Files archived'
      ]
    }
  ],
  'Publication': [
    {
      name: 'Phase 1: Planning & Strategy',
      owner: 'Strategy Division',
      duration: '1-2 weeks',
      activities: [
        'Publication goals and objectives',
        'Target audience definition',
        'Content strategy development',
        'Editorial calendar creation',
        'Ad coordination and specification',
        'Budget and resource planning'
      ],
      deliverables: [
        'Publication Strategy Document',
        'Editorial calendar (6-12 months)',
        'Content guidelines and standards',
        'Budget and timeline'
      ],
      criteriaToProceed: [
        'Strategy approved by stakeholders',
        'Editorial calendar finalized',
        'Budget confirmed',
        'Content contributors identified'
      ]
    },
    {
      name: 'Phase 2: Content Development',
      owner: 'Content Team',
      duration: '4-8 weeks',
      activities: [
        'Article writing and gathering',
        'Interviews and research',
        'Image sourcing and creation',
        'Content editing',
        'Fact-checking',
        'Legal review (if needed)'
      ],
      deliverables: [
        'All articles written and edited',
        'Images selected and licensed',
        'Infographics and data visualizations',
        'Table of contents finalized'
      ],
      criteriaToProceed: [
        'All content approved by editor'
      ]
    },
    {
      name: 'Phase 3: Design & Layout',
      owner: 'Production Division (Design)',
      duration: '3-4 weeks',
      activities: [
        'Cover design',
        'Interior layout and typography',
        'Image placement and sizing',
        'Ad placement',
        'Section design',
        'Design revisions',
        'Final proofing'
      ],
      deliverables: [
        'Designed cover (front, back, spine)',
        'Complete interior layout',
        'Style guide documentation',
        'Digital proof for review'
      ],
      criteriaToProceed: [
        'Cover approved',
        'All layouts approved',
        'Ad Placement Confirmed',
        'Final proofreading completed',
        'Print-ready files verified'
      ]
    },
    {
      name: 'Phase 4: Production & Distribution',
      owner: 'Production Division',
      duration: '2-4 weeks',
      activities: [
        'File preparation for print/digital',
        'Printer coordination (if print)',
        'Digital publication setup (if digital)',
        'Quality control',
        'Distribution setup',
        'Launch coordination'
      ],
      deliverables: [
        'Printed publications (if applicable)',
        'Digital publication files',
        'Distribution channels activated',
        'Launch materials'
      ],
      criteriaToProceed: [
        'Print quality verified',
        'Digital versions published',
        'Distribution channels live',
        'Launch completed',
        'Success metrics tracking initiated'
      ]
    }
  ],
  'SEO': [
    {
      name: 'Phase 1: Audit & Strategy',
      owner: 'Insights & Innovation Division',
      duration: '2 weeks',
      activities: [
        'Comprehensive SEO audit',
        'Keyword research and analysis',
        'Competitive analysis',
        'Technical SEO assessment',
        'Content gap analysis',
        'Strategy development'
      ],
      deliverables: [
        'SEO Audit Report',
        'Keyword Research Report',
        'Competitive Analysis',
        'SEO Strategy Document',
        'Prioritized recommendations'
      ],
      criteriaToProceed: [
        'Audit completed and reviewed',
        'Strategy approved by client',
        'Keywords prioritized',
        'Timeline and milestones agreed upon'
      ]
    },
    {
      name: 'Phase 2: Technical Optimization',
      owner: 'Production Division (Development)',
      duration: '2-4 weeks',
      activities: [
        'Site speed optimization',
        'Mobile optimization',
        'URL structure improvements',
        'Schema markup implementation',
        'XML sitemap optimization',
        'Robots.txt configuration',
        'Canonical tag implementation',
        'Technical issues resolution'
      ],
      deliverables: [
        'Optimized site structure',
        'Improved page load speeds',
        'Technical SEO implementation',
        'Technical documentation'
      ],
      criteriaToProceed: [
        'All technical issues resolved',
        'Site speed benchmarks met',
        'Mobile optimization verified',
        'Technical SEO audit passed',
        'Google Search Console verified'
      ]
    },
    {
      name: 'Phase 3: On-Page Optimization',
      owner: 'Content Team + Insights Division',
      duration: '3-6 weeks',
      activities: [
        'Meta title and description optimization',
        'Header tag optimization',
        'Content optimization',
        'Internal linking strategy',
        'Image optimization (alt tags, file names)',
        'Content creation (if needed)',
        'Quality assurance'
      ],
      deliverables: [
        'Optimized meta tags for all pages',
        'Updated content',
        'Internal linking structure',
        'Image optimization completed',
        'On-page SEO documentation'
      ],
      criteriaToProceed: [
        'All priority pages optimized',
        'Content approved and published',
        'Internal linking implemented',
        'Quality assurance completed',
        'Client approval received'
      ]
    },
    {
      name: 'Phase 4: Monitoring & Ongoing Optimization',
      owner: 'Insights & Innovation Division',
      duration: 'Ongoing',
      activities: [
        'Ranking tracking',
        'Traffic monitoring',
        'Conversion tracking',
        'Monthly reporting',
        'Ongoing content optimization',
        'Link building activities',
        'Algorithm update monitoring',
        'Quarterly strategy reviews'
      ],
      deliverables: [
        'Monthly SEO reports',
        'Quarterly strategy reviews',
        'Ongoing optimization recommendations'
      ],
      criteriaToProceed: [
        '90-day performance review completed',
        'Ranking improvements verified',
        'Client satisfied with progress',
        'Ongoing plan established'
      ]
    }
  ],
  'Social Media': [
    {
      name: 'Phase 1: Strategy & Planning',
      owner: 'Strategy Division',
      duration: '1-2 weeks',
      activities: [
        'Social media audit',
        'Audience research and persona development',
        'Competitive analysis',
        'Platform selection, prioritization and access',
        'Content strategy development',
        'KPI definition',
        'Content calendar planning'
      ],
      deliverables: [
        'Social Media Strategy Document',
        'Audience personas',
        'Content calendar template',
        'KPI framework',
        'Brand voice and guidelines'
      ],
      criteriaToProceed: [
        'Strategy approved by client',
        'Platforms confirmed',
        'KPIs agreed upon',
        'Content calendar structure approved',
        'Brand guidelines finalized'
      ]
    },
    {
      name: 'Phase 2: Content Creation',
      owner: 'Production Division (Creative)',
      duration: '2-3 weeks (initial batch)',
      activities: [
        'Content copywriting',
        'Graphic design and visual creation',
        'Video production (if applicable)',
        'Photo sourcing/editing',
        'Content scheduling',
        'Keyword/hashtag research',
        'Review and approvals'
      ],
      deliverables: [
        '30-day content calendar (minimum)',
        'Designed graphics and visuals',
        'Video content (if applicable)',
        'Copy for all posts',
        'Content approval documentation'
      ],
      criteriaToProceed: [
        'First month of content approved',
        'All assets created and approved',
        'Content scheduled in management tool',
        'Client trained on review process',
        'Emergency content plan in place'
      ]
    },
    {
      name: 'Phase 3: Launch & Community Management',
      owner: 'Account Manager + Insights Division',
      duration: 'Ongoing',
      activities: [
        'Content publishing',
        'Community engagement and response',
        'Monitoring and listening',
        'Performance tracking',
        'Weekly optimization',
        'Trend identification',
        'Crisis monitoring'
      ],
      deliverables: [
        'Published social content',
        'Daily community management',
        'Weekly engagement reports',
        'Monthly performance reports'
      ],
      criteriaToProceed: [
        '30-day publish record established',
        'Community management protocols working',
        'Engagement metrics tracked',
        'First monthly review completed'
      ]
    },
    {
      name: 'Phase 4: Optimization & Growth',
      owner: 'Insights & Innovation Division',
      duration: 'Ongoing',
      activities: [
        'Performance analysis',
        'A/B testing',
        'Paid social campaign management (if applicable)',
        'Influencer partnerships (if applicable)',
        'Content optimization',
        'Monthly strategy reviews',
        'Quarterly planning'
      ],
      deliverables: [
        'Monthly analytics reports',
        'Quarterly strategy reviews',
        'Growth recommendations',
        'Updated content strategies'
      ],
      criteriaToProceed: [
        '90-day performance review completed',
        'Growth trends identified',
        'Optimization opportunities implemented',
        'Client satisfied with engagement'
      ]
    }
  ],
  'Video': [
    {
      name: 'Phase 1: Concept & Pre-Production',
      owner: 'Strategy Division + Production Division',
      duration: '1-2 weeks',
      activities: [
        'Creative brief and goals definition',
        'Target audience research',
        'Concept development',
        'Scriptwriting',
        'Storyboarding',
        'Budget and timeline planning',
        'Location scouting (if live-action)',
        'Casting (if live-action)',
        'Shot list creation (live-action) or animation style frames (motion graphics)',
        'Asset list and visual style guide (if motion graphics/animation)'
      ],
      deliverables: [
        'Creative Brief',
        'Script (approved)',
        'Storyboard',
        'Shot list (live-action) or animation style frames (motion graphics)',
        'Production schedule',
        'Budget breakdown'
      ],
      criteriaToProceed: [
        'Script approved by client',
        'Storyboard approved',
        'Budget confirmed',
        'All talent/locations confirmed (if live-action)',
        'Equipment and crew scheduled (if live-action) or animation software/tools ready (if motion graphics)',
        'Shot list (live-action) or style frames (motion graphics) finalized'
      ]
    },
    {
      name: 'Phase 2: Production',
      owner: 'Production Division (Video)',
      duration: '1-5 days (live-action) or 2-4 weeks (motion graphics)',
      activities: [
        'Equipment setup (if live-action)',
        'Video filming/recording (if live-action)',
        'B-roll capture (if live-action)',
        'Audio recording (if live-action)',
        'Multiple takes as needed (if live-action)',
        'On-set direction (if live-action)',
        'Voiceover recording',
        'Animation asset creation (if motion graphics)',
        'Character rigging (if motion graphics)',
        'Scene building and animation (if motion graphics)',
        'Backup and file management'
      ],
      deliverables: [
        'Raw footage/shots (if live-action) or animation renders (if motion graphics)',
        'Audio files',
        'B-roll footage (if live-action)',
        'Production notes',
        'Backup files secured'
      ],
      criteriaToProceed: [
        'All shots captured per shot list (if live-action) or all scenes animated (if motion graphics)',
        'Audio quality verified',
        'Client/stakeholder approved footage or animation',
        'All files backed up',
        'Production wrap confirmed'
      ]
    },
    {
      name: 'Phase 3: Post-Production',
      owner: 'Production Division (Video/Edit)',
      duration: '2-4 weeks',
      activities: [
        'Video editing (rough cut) or animation compositing',
        'Client review and revisions',
        'Fine cut editing',
        'Color correction and grading (if live-action)',
        'Audio mixing and sound design',
        'Motion graphics and titles',
        'Music selection and licensing',
        'Final revisions',
        'Final export in required formats'
      ],
      deliverables: [
        'Rough cut for review',
        'Fine cut for approval',
        'Final video (all required formats)',
        'Captions/subtitles (if required)',
        'Project files archived'
      ],
      criteriaToProceed: [
        'Final cut approved by client',
        'All revisions completed',
        'Audio mix approved',
        'Color grading approved (if live-action)',
        'Final files delivered in all formats',
        'Captions/subtitles approved (if applicable)'
      ]
    },
    {
      name: 'Phase 4: Distribution & Optimization',
      owner: 'Insights & Innovation Division',
      duration: '1-2 weeks + ongoing',
      activities: [
        'Video publishing (YouTube, Vimeo, social, website)',
        'SEO optimization (titles, descriptions, tags)',
        'Thumbnail creation and optimization',
        'Promotional content creation',
        'Performance tracking setup',
        'Paid promotion (if applicable)',
        'Engagement monitoring',
        'Performance reporting'
      ],
      deliverables: [
        'Published video on all platforms',
        'Optimized metadata',
        'Promotional materials',
        'Performance tracking dashboard',
        'Monthly performance reports'
      ],
      criteriaToProceed: [
        'Video live on all platforms',
        'SEO optimization complete',
        'Tracking verified',
        '30-day performance review completed',
        'Optimization recommendations provided'
      ]
    }
  ],
  'Website': [
    {
      name: 'Phase 0: Project Kickoff',
      owner: 'Account Manager',
      duration: '1 week',
      activities: [
        'Project Brief (client goals, timeline, budget)',
        'Kick-off meeting with all division leads',
        'Roles & Responsibilities Matrix assigned'
      ],
      deliverables: [
        'Project Brief',
        'Kick-off meeting notes',
        'Roles & Responsibilities Matrix'
      ],
      criteriaToProceed: [
        'Project Brief approved',
        'All division leads attended kickoff',
        'Roles assigned and confirmed'
      ]
    },
    {
      name: 'Phase 1: Strategy',
      owner: 'Strategy Division',
      duration: '2-3 weeks',
      activities: [
        'Discovery and client interviews',
        'Competitive analysis review',
        'User journey mapping',
        'Strategic positioning'
      ],
      deliverables: [
        'Creative Brief + Site Specification Document (complete sitemap, page specs, user flows, technical requirements)',
        'Content Requirements Matrix (all content pieces, types, sources, owners, dates)'
      ],
      criteriaToProceed: [
        'Creative Brief signed by all parties',
        'Site Specification Document complete',
        'Content Requirements Matrix signed with all owners/dates committed',
        'Technical requirements reviewed by development team',
        'Budget and timeline confirmed based on scope',
        'All divisions confirm they have what they need to proceed'
      ]
    },
    {
      name: 'Phase 2: Content',
      owner: 'Account Manager (coordination) + Client (delivery)',
      duration: '3-6 weeks',
      activities: [
        'Content gathering from client',
        'Content creation (copywriting, if in scope)',
        'Image sourcing/selection',
        'Video/media asset collection',
        'Content review and editing'
      ],
      deliverables: [
        'Content Approval Package (all written content finalized, all images selected, all media assets delivered)',
        'Content organized by page/section per sitemap',
        'Signed "Content Lock Agreement" from client'
      ],
      criteriaToProceed: [
        '100% of content delivered per Content Requirements Matrix',
        'Client signature on "Content Lock Agreement"',
        'Content reviewed for brand voice and messaging alignment',
        'All assets in correct formats and specifications',
        'Backup/contingency content identified for any gaps'
      ]
    },
    {
      name: 'Phase 3: Design',
      owner: 'Production Division (Design)',
      duration: '3-5 weeks',
      activities: [
        'Design boards (aesthetic direction)',
        'Homepage design',
        'Key secondary page designs',
        'Template designs for remaining pages',
        'Mobile responsive designs',
        'Design revision rounds (1-2 rounds)'
      ],
      deliverables: [
        'Complete Design Package (all unique page designs, all template designs)',
        'Mobile/tablet/desktop views',
        'Interactive element specifications',
        'Design system documentation',
        'Style guide'
      ],
      criteriaToProceed: [
        'All designs approved by client',
        'All interactive behaviors specified',
        'Design system documented for developers',
        'Technical feasibility confirmed by development team',
        'Client acknowledges design is final'
      ]
    },
    {
      name: 'Phase 4: Front-End Code',
      owner: 'Production Division (Development - Front-End)',
      duration: '3-4 weeks',
      activities: [
        'HTML/CSS/JavaScript development',
        'Responsive framework implementation',
        'Cross-browser testing',
        'Accessibility implementation',
        'Performance optimization',
        'Front-end QA'
      ],
      deliverables: [
        'Static HTML Build (fully coded static site)',
        'All pages/templates completed',
        'Responsive across devices',
        'Browser compatibility tested',
        'Accessibility compliant',
        'Performance benchmarks met'
      ],
      criteriaToProceed: [
        'All pages coded and functional',
        'Cross-browser testing passed',
        'Mobile responsiveness verified',
        'Accessibility audit passed',
        'Performance metrics meet standards',
        'Code review completed',
        'Back-end team confirms readiness to integrate'
      ]
    },
    {
      name: 'Phase 5: Back-End Code',
      owner: 'Production Division (Development - Back-End)',
      duration: '2-4 weeks',
      activities: [
        'ExpressionEngine CMS integration',
        'Template conversion',
        'Content population',
        'Third-party integrations (forms, analytics, etc.)',
        'Database setup',
        'Admin training preparation',
        'Staging environment setup',
        'Full QA and testing'
      ],
      deliverables: [
        'Functional CMS Website',
        'All content populated',
        'All functionality tested',
        'Admin documentation',
        'Training materials prepared',
        'Staging site for client review',
        'Pre-launch checklist completed'
      ],
      criteriaToProceed: [
        'All functionality tested and working',
        'Content fully populated and proofed',
        'Client review on staging completed',
        'Forms and integrations tested',
        'Analytics tracking verified',
        'SEO elements implemented',
        'Security audit passed',
        'Backup systems in place',
        'Launch plan documented',
        'Client training completed',
        'Insights pre-launch audit passed'
      ]
    },
    {
      name: 'Phase 6: Insights & Innovation (Post-Launch)',
      owner: 'Insights & Innovation Division',
      duration: 'Ongoing',
      activities: [
        'Site launch support',
        'Performance monitoring (30/60/90 day checkpoints)',
        'Analytics review and reporting',
        'SEO optimization',
        'Ongoing maintenance',
        'Content updates and improvements',
        'A/B testing and optimization',
        'Strategic recommendations for improvements'
      ],
      deliverables: [
        'Launch Package (launch checklist, DNS/hosting transition, 48hr monitoring)',
        '30-Day Post-Launch Review (performance analytics, user behavior analysis, technical audit)',
        'Ongoing Support (monthly analytics, quarterly strategy reviews, optimization recommendations)'
      ],
      criteriaToProceed: [
        '30-day performance review completed',
        'Client satisfied with launch outcomes',
        'All punch-list items resolved',
        'Maintenance agreement in place',
        'Handoff to ongoing support complete'
      ]
    }
  ],
  'Voyager': [
    {
      name: 'Voyager Setup',
      owner: 'Aiden McKenna',
      duration: 'Ongoing',
      activities: [
        'Monthly SEO reporting and optimization',
        'Analytics monitoring',
        'Technical audits',
        'Content recommendations'
      ],
      deliverables: [
        'Monthly reports',
        'SEO recommendations',
        'Performance analytics'
      ],
      criteriaToProceed: [
        'Advanced Report',
        'Analytics & Access',
        'Search Console',
        'Semrush',
        'Keywords',
        'Home Schema',
        'FAQ page',
        'Top Schema',
        'Local Search',
        'LLMS Text',
        'Lighthouse Audit',
        'Spambot Audit'
      ]
    }
  ]
};

interface Client {
  id: number;
  name: string;
  company?: string;
  contact_email?: string;
  contact_phone?: string;
  account_manager_id?: number;
  health_score: number;
  satisfaction_rating: number;
  active_campaigns: number;
  total_projects: number;
  total_tasks: number;
  avg_campaign_progress: number;
}

interface DashboardData {
  clients: Client[];
}

type UserRole = 'account-manager' | 'project-manager' | 'technician';

interface Campaign {
  id: number;
  name: string;
  description: string;
  status: string;
  progress_percentage: number;
  budget: number;
  start_date: string;
  end_date: string;
  project_count: number;
  client_name?: string;
  client_id?: number;
  account_manager_id?: number;
  current_phase?: string;
  current_phase_number?: number;
  phase_status?: string;
}

interface Project {
  id: number;
  name: string;
  description: string;
  project_manager: string;
  status: string;
  progress_percentage: number;
  priority: string;
  campaign_name: string;
  client_name: string;
  task_count: number;
  completed_tasks: number;
  blocked_tasks: number;
  project_type?: string;
  campaign_id?: number;
  client_id?: number;
  voyager_level?: string;
  client_url?: string;
  created_at?: string;
  current_phase?: number;
  phase?: string;
  account_manager_id?: number;
  account_manager_name?: string;
  display_order?: number;
}

interface Task {
  id: number;
  name: string;
  description: string;
  assigned_to: string;
  created_by?: string;
  due_date: string;
  status: string;
  priority: string;
  phase: string;
  project_name: string;
  project_manager: string;
  campaign_name: string;
  client_name: string;
  account_manager: string;
  estimated_hours: number;
  actual_hours: number;
  updated_at: string;
}

interface AccountManager {
  id: number;
  name: string;
  email: string;
  phone: string;
  department: string;
}

interface AccountManagerPriority {
  id: number;
  account_manager_id: number;
  title: string;
  description: string;
  due_date: string;
  status: string;
  priority: string;
  resource?: string;
  created_at: string;
  updated_at: string;
  display_order?: number;
}

// Map URLs to view names
const URL_TO_VIEW: { [key: string]: string } = {
  '/': 'overview',
  '/overview': 'overview',
  '/dashboard': 'overview',
  '/accounts': 'account-management',
  '/account-management': 'account-management',
  '/client-detail': 'client-detail',
  '/projects': 'projects',
  '/project-management': 'project-management',
  '/project-detail': 'project-detail',
  '/project-details': 'project-details',
  '/campaigns': 'campaigns',
  '/campaign-detail': 'campaign-detail',
  '/team-tasks': 'team-tasks',
  '/priorities': 'priorities',
  '/account-managers': 'account-managers',
  '/users': 'users',
  '/activity-log': 'activity-log',
  '/technician': 'technician',
};

// Map view names to URLs
const VIEW_TO_URL: { [key: string]: string } = {
  'overview': '/overview',
  'account-management': '/accounts',
  'client-detail': '/client-detail',
  'projects': '/projects',
  'project-management': '/project-management',
  'project-detail': '/project-detail',
  'project-details': '/project-details',
  'campaigns': '/campaigns',
  'campaign-detail': '/campaign-detail',
  'team-tasks': '/team-tasks',
  'priorities': '/priorities',
  'account-managers': '/account-managers',
  'users': '/users',
  'activity-log': '/activity-log',
  'technician': '/technician',
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize view from URL
  const [currentView, setCurrentView] = useState(() => {
    return URL_TO_VIEW[location.pathname] || 'overview';
  });

  // Sync view when URL changes (browser back/forward)
  useEffect(() => {
    const viewFromUrl = URL_TO_VIEW[location.pathname];
    if (viewFromUrl && viewFromUrl !== currentView) {
      setCurrentView(viewFromUrl);
    }
  }, [location.pathname]);

  // Handle taskId query parameter from Slack notifications
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const taskId = searchParams.get('taskId');

    if (taskId) {
      // Fetch the task and open the edit modal
      axios.get(`${API_URL}/api/tasks/${taskId}`)
        .then(response => {
          if (response.data) {
            setEditingTask(response.data);
            setShowEditTaskModal(true);
            // Remove the query parameter to clean up the URL
            navigate(location.pathname, { replace: true });
          }
        })
        .catch(error => {
          console.error('Failed to load task from URL:', error);
          showNotification('error', 'Task not found');
        });
    }
  }, [location.search]);

  // Helper function to update view and URL
  const updateView = (newView: string) => {
    setCurrentView(newView);
    const newUrl = VIEW_TO_URL[newView] || '/overview';
    if (location.pathname !== newUrl) {
      navigate(newUrl);
    }
    // Reload tasks when navigating to priorities view
    if (newView === 'priorities') {
      loadAllTeamTasks();
    }
  };

  const [userRole] = useState<UserRole>('account-manager');
  const [currentUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        const user = JSON.parse(savedUser);
        return user.name || user.full_name || 'Unknown User';
      }
      return 'Unknown User';
    } catch (error) {
      console.error('Error loading user from localStorage:', error);
      return 'Unknown User';
    }
  });
  const [dashboardData, setDashboardData] = useState<DashboardData>({ clients: [] });
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([]);
  const [campaignSearchQuery, setCampaignSearchQuery] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [teamTaskSearchQuery, setTeamTaskSearchQuery] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [campaignProjects, setCampaignProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [projectPhases, setProjectPhases] = useState<any[]>([]);
  const [selectedPhaseId, setSelectedPhaseId] = useState<number | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [taskComments, setTaskComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [topProjects, setTopProjects] = useState<any[]>([]);
  const [recentCompletions, setRecentCompletions] = useState<any[]>([]);
  const [accountManagers, setAccountManagers] = useState<AccountManager[]>([]);
  const [accountManagerStats, setAccountManagerStats] = useState<any[]>([]);
  const [selectedAccountManagerId, setSelectedAccountManagerId] = useState<number | null>(null);
  const [accountManagerClients, setAccountManagerClients] = useState<any[]>([]);
  const [editingClientContact, setEditingClientContact] = useState<any | null>(null);
  // const [accountManagerPriorities, setAccountManagerPriorities] = useState<{[key: number]: AccountManagerPriority[]}>({}); // Not used - projects are the priorities
  const [showInProgress, setShowInProgress] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected'>('disconnected');
  const [loading, setLoading] = useState(true);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showNewCampaignModal, setShowNewCampaignModal] = useState(false);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showMoveProjectModal, setShowMoveProjectModal] = useState(false);
  const [projectToMove, setProjectToMove] = useState<Project | null>(null);
  const [moveProjectData, setMoveProjectData] = useState({ client_id: '', campaign_id: '' });
  const [showQuickEditModal, setShowQuickEditModal] = useState(false);
  const [quickEditProject, setQuickEditProject] = useState<Project | null>(null);
  const [projectCreationMode, setProjectCreationMode] = useState<'quick' | 'advanced'>('quick');
  const [selectedProjectType, setSelectedProjectType] = useState('');
  const [voyagerLevel, setVoyagerLevel] = useState('');
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showNewPriorityModal, setShowNewPriorityModal] = useState(false);
  const [prioritiesViewMode, setPrioritiesViewMode] = useState<'team' | 'phase'>('team');
  const [isReorderingTeamMembers, setIsReorderingTeamMembers] = useState(false);
  const [teamPrioritiesViewMode, setTeamPrioritiesViewMode] = useState<'team' | 'phase'>('team');
  const [isReorderingTeamPriorityMembers, setIsReorderingTeamPriorityMembers] = useState(false);
  const [selectedAccountManagerForPriority, setSelectedAccountManagerForPriority] = useState<number | null>(null);
  const [showClientCampaignSelectModal, setShowClientCampaignSelectModal] = useState(false);
  const [selectedClientForProject, setSelectedClientForProject] = useState<number | null>(null);
  const [selectedCampaignForProject, setSelectedCampaignForProject] = useState<number | null>(null);
  const [showNewClientInline, setShowNewClientInline] = useState(false);
  const [showNewCampaignInline, setShowNewCampaignInline] = useState(false);
  const [showPriorityDetailModal, setShowPriorityDetailModal] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState<AccountManagerPriority | null>(null);
  const [editingPriority, setEditingPriority] = useState<AccountManagerPriority | null>(null);
  const [showBulkTaskModal, setShowBulkTaskModal] = useState(false);
  const [bulkTaskText, setBulkTaskText] = useState('');
  const [bulkTaskPreview, setBulkTaskPreview] = useState<any[]>([]);
  const [showDiscoveryForm, setShowDiscoveryForm] = useState(false);
  const [discoveryClientId, setDiscoveryClientId] = useState<number | null>(null);
  const [discoveryProjectType, setDiscoveryProjectType] = useState<string>('');
  const [newClientData, setNewClientData] = useState({ name: '', company: '', contact_email: '', contact_phone: '', account_manager_id: '' });
  const [newCampaignData, setNewCampaignData] = useState({ client_id: '', name: '', description: '', budget: '', start_date: '', end_date: '', account_manager_id: '' });
  const [newProjectData, setNewProjectData] = useState({ campaign_id: '', name: '', description: '', project_manager: '', priority: 'Medium', account_manager_id: '' });
  const [newTaskData, setNewTaskData] = useState({ project_id: '', name: '', description: '', assigned_to: '', due_date: '', priority: 'Medium', phase: 'Strategy', estimated_hours: '' });
  const [newPriorityData, setNewPriorityData] = useState({ title: '', description: '', due_date: '', priority: 'Medium', status: 'Not Started', resource: '' });
  const [taskFiles, setTaskFiles] = useState<File[]>([]);
  const [campaignFiles, setCampaignFiles] = useState<File[]>([]);
  const [projectFiles, setProjectFiles] = useState<File[]>([]);
  const [teamMembers, setTeamMembers] = useState<Array<{id: number, name: string, email?: string, display_order?: number}>>([]);
  const [roles, setRoles] = useState<Array<{id: number, name: string, description: string}>>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [userFormData, setUserFormData] = useState({ name: '', email: '', role_id: '', password: '', slack_user_id: '' });
  const [teamTasksFilter, setTeamTasksFilter] = useState<'all' | 'assignee' | 'account_manager' | 'phase'>('all');
  const [selectedAssignee, setSelectedAssignee] = useState<string>(() => {
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        const user = JSON.parse(savedUser);
        return user.name || user.full_name || '';
      }
      return '';
    } catch (error) {
      return '';
    }
  });
  const [selectedAccountManager, setSelectedAccountManager] = useState<string>('');
  const [selectedPhase, setSelectedPhase] = useState<string>('');
  const [selectedProjectFilter, setSelectedProjectFilter] = useState<string>('');
  // const [teamTasksGroupBy, setTeamTasksGroupBy] = useState<string>(''); // Removed - unused
  const [taskSortBy, setTaskSortBy] = useState<'status' | 'priority' | 'due_date'>('priority');
  const [hideCompletedTasks, setHideCompletedTasks] = useState(true);
  const [hideProjectCompletedTasks, setHideProjectCompletedTasks] = useState(true);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [showEditCampaignModal, setShowEditCampaignModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState<'client' | 'campaign' | 'project' | 'task' | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [notifications, setNotifications] = useState<Array<{ id: number; type: 'success' | 'error'; message: string }>>([]);

  useEffect(() => {
    checkConnection();
    loadDashboardData();
    loadTopProjects();
    loadRecentCompletions();
    loadTeamMembers();
    loadRoles(); // Load roles for user management
    loadAllTeamTasks(); // Load all tasks for the overview
    loadAccountManagers(); // Load account managers for dropdowns
  }, []);

  // Notification system
  const showNotification = (type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message }]);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const dismissNotification = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    if (currentView === 'project-management') {
      loadProjectManagementData();
    } else if (currentView === 'technician' && userRole === 'technician') {
      loadTechnicianTasks();
    } else if (currentView === 'campaigns') {
      loadAllCampaigns();
    } else if (currentView === 'team-tasks') {
      loadAllTeamTasks();
    } else if (currentView === 'priorities') {
      loadAllTeamTasks();
      loadTeamMembers();
    } else if (currentView === 'account-managers') {
      loadAccountManagers();
    }
  }, [currentView, userRole]);

  useEffect(() => {
    console.log('showBulkTaskModal changed to:', showBulkTaskModal);
  }, [showBulkTaskModal]);

  // Load projects when viewing Voyager or Account Priorities tab
  useEffect(() => {
    if (currentView === 'voyager' || currentView === 'account-managers') {
      loadAllProjects();
    }
  }, [currentView]);

  const checkConnection = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/health`);
      if (response.status === 200) {
        setConnectionStatus('connected');
      }
    } catch (error) {
      console.error('Connection check failed:', error);
      setConnectionStatus('disconnected');
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/team-members`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/team-members/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserFormData({ name: '', email: '', role_id: '', password: '', slack_user_id: '' });
    setShowUserModal(true);
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setUserFormData({
      name: user.name,
      email: user.email,
      role_id: user.role_id?.toString() || '',
      password: '', // Don't pre-fill password
      slack_user_id: user.slack_user_id || ''
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        // Update existing user
        const updateData: any = {
          name: userFormData.name,
          email: userFormData.email,
          role_id: parseInt(userFormData.role_id),
          slack_user_id: userFormData.slack_user_id || null
        };

        // Only include password if it was changed
        if (userFormData.password) {
          updateData.password = userFormData.password;
        }

        await axios.put(`${API_URL}/api/team-members/${editingUser.id}`, updateData);
        showNotification('success', 'User updated successfully');
      } else {
        // Create new user
        await axios.post(`${API_URL}/api/team-members`, {
          name: userFormData.name,
          email: userFormData.email,
          role_id: parseInt(userFormData.role_id),
          password: userFormData.password || 'resetme',
          slack_user_id: userFormData.slack_user_id || null
        });
        showNotification('success', 'User created successfully');
      }

      setShowUserModal(false);
      loadTeamMembers();
    } catch (error: any) {
      console.error('Failed to save user:', error);
      showNotification('error', error.response?.data?.error || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (user: any) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}? This will deactivate their account.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/team-members/${user.id}`);
      showNotification('success', 'User deleted successfully');
      loadTeamMembers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      showNotification('error', 'Failed to delete user');
    }
  };

  const loadAccountManagerStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/team-members/account-manager-stats`);
      setAccountManagerStats(response.data);
    } catch (error) {
      console.error('Failed to load account manager stats:', error);
    }
  };

  const loadAccountManagerClients = async (accountManagerId: number) => {
    try {
      const response = await axios.get(`${API_URL}/api/team-members/${accountManagerId}/clients`);
      setAccountManagerClients(response.data);
    } catch (error) {
      console.error('Failed to load account manager clients:', error);
    }
  };

  const loadTopProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/top-by-velocity`);
      setTopProjects(response.data);
    } catch (error) {
      console.error('Failed to load top projects:', error);
    }
  };

  const loadRecentCompletions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tasks/recent-completions`);
      setRecentCompletions(response.data);
    } catch (error) {
      console.error('Failed to load recent completions:', error);
    }
  };

  const loadTaskComments = async (taskId: number) => {
    try {
      const response = await axios.get(`${API_URL}/api/tasks/${taskId}/comments`);
      setTaskComments(response.data);
    } catch (error) {
      console.error('Failed to load task comments:', error);
    }
  };

  const uploadCommentFile = async (taskId: number, file: File): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append('files', file);
      formData.append('entity_type', 'task_comment');
      formData.append('entity_id', taskId.toString());

      // Don't set Content-Type header - axios will set it automatically with boundary
      const response = await axios.post(`${API_URL}/api/uploads`, formData);

      if (response.data.success && response.data.attachments && response.data.attachments.length > 0) {
        const attachment = response.data.attachments[0];
        return `${API_URL}/uploads/${attachment.filename}`;
      }
      return null;
    } catch (error) {
      console.error('Failed to upload file:', error);
      showNotification('error', 'Failed to upload file');
      return null;
    }
  };

  const handleCommentFileUpload = async (file: File) => {
    if (!selectedTask) return;

    showNotification('success', 'Uploading file...');
    const fileUrl = await uploadCommentFile(selectedTask.id, file);

    if (fileUrl) {
      const fileLink = `<p><a href="${fileUrl}" target="_blank" style="color: #3b82f6; text-decoration: underline;">${file.name}</a></p>`;
      setNewComment(newComment + fileLink);
      showNotification('success', 'File uploaded successfully');
    }
  };

  const addTaskComment = async (taskId: number, comment: string) => {
    try {
      const response = await axios.post(`${API_URL}/api/tasks/${taskId}/comments`, {
        user_name: currentUser || 'Unknown User',
        comment: comment
      });
      if (response.data.success) {
        await loadTaskComments(taskId);
        setNewComment('');
        showNotification('success', 'Comment added successfully');
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
      showNotification('error', 'Failed to add comment');
    }
  };

  const loadCampaignsForClient = async (clientId: number) => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${clientId}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const loadAllCampaigns = async () => {
    try {
      // Load all campaigns from all clients for the dropdown
      const clientsResponse = await axios.get(`${API_URL}/api/clients`);
      const allCampaignsData = [];

      for (const client of clientsResponse.data) {
        const campaignsResponse = await axios.get(`${API_URL}/api/clients/${client.id}/campaigns`);
        const campaignsWithClient = campaignsResponse.data.map((campaign: any) => ({
          ...campaign,
          client_name: client.name
        }));
        allCampaignsData.push(...campaignsWithClient);
      }

      setAllCampaigns(allCampaignsData);
    } catch (error) {
      console.error('Failed to load all campaigns:', error);
    }
  };

  const loadProjectManagementData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/project-management`);
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Failed to load project management data:', error);
    }
  };

  const loadAllProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/project-management`);
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadTechnicianTasks = async () => {
    try {
      // For demo, we'll load tasks for a specific team member
      const teamMember = 'Alex Thompson'; // In real app, this would be the logged-in user
      const response = await axios.get(`${API_URL}/api/tasks/assigned/${encodeURIComponent(teamMember)}`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load technician tasks:', error);
    }
  };

  const loadAllTeamTasks = async () => {
    try {
      // Load all tasks across all projects
      const response = await axios.get(`${API_URL}/api/tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load all team tasks:', error);
    }
  };

  const loadAccountManagers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/account-managers`);
      const managers = response.data;
      setAccountManagers(managers);

      // Projects are the priorities - no separate priority list needed
    } catch (error) {
      console.error('Failed to load account managers:', error);
    }
  };

  const loadProjectsForCampaign = async (campaignId: number) => {
    try {
      const response = await axios.get(`${API_URL}/api/campaigns/${campaignId}/projects`);
      setCampaignProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects for campaign:', error);
    }
  };

  const handleCampaignClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    loadProjectsForCampaign(campaign.id);
    updateView('campaign-detail');
  };

  const handleProceedToProjectCreation = () => {
    if (selectedCampaignForProject) {
      setNewProjectData({
        ...newProjectData,
        campaign_id: selectedCampaignForProject.toString()
      });
      setShowClientCampaignSelectModal(false);
      setShowNewProjectModal(true);
    }
  };

  const loadTasksForProject = async (projectId: number) => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/${projectId}/tasks`);
      setProjectTasks(response.data);
    } catch (error) {
      console.error('Failed to load tasks for project:', error);
    }
  };

  const loadPhasesForProject = async (projectId: number) => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/${projectId}/phases`);
      // Sort by phase_number to ensure correct order
      const sortedPhases = response.data.sort((a: any, b: any) => a.phase_number - b.phase_number);
      setProjectPhases(sortedPhases);
      // Set the active phase as selected by default
      const activePhase = sortedPhases.find((p: any) => p.status === 'active');
      if (activePhase) {
        setSelectedPhaseId(activePhase.id);
      } else if (sortedPhases.length > 0) {
        setSelectedPhaseId(sortedPhases[0].id);
      }
    } catch (error) {
      console.error('Failed to load phases for project:', error);
      setProjectPhases([]);
    }
  };

  const handleCriteriaToggle = async (criteriaId: number, isCompleted: boolean) => {
    try {
      await axios.patch(`${API_URL}/api/criteria/${criteriaId}`, {
        is_completed: isCompleted,
        completed_by: 'Current User' // TODO: Replace with actual user
      });

      // Reload phases to get updated state
      if (selectedProject) {
        loadPhasesForProject(selectedProject.id);
      }
    } catch (error) {
      console.error('Failed to update criteria:', error);
      showNotification('error', 'Failed to update criteria');
    }
  };

  const handleAdvancePhase = async () => {
    if (!selectedProject) return;

    try {
      const response = await axios.post(`${API_URL}/api/projects/${selectedProject.id}/advance-phase`);
      if (response.data.success) {
        showNotification('success', 'Advanced to next phase!');
        loadPhasesForProject(selectedProject.id);
      }
    } catch (error) {
      console.error('Failed to advance phase:', error);
      showNotification('error', 'Failed to advance phase');
    }
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    loadTasksForProject(project.id);
    loadPhasesForProject(project.id);
    updateView('project-detail');
  };

  // const handleTaskClick = (task: Task) => {
  //   setSelectedTask(task);
  //   setShowTaskDetailModal(true);
  // }; // Removed - unused

  const updateTaskStatus = async (taskId: number, status: string, actualHours: number) => {
    try {
      await axios.put(`${API_URL}/api/tasks/${taskId}/status`, {
        status,
        updated_by: currentUser
      });
      // Reload tasks
      if (currentView === 'technician') {
        loadTechnicianTasks();
      } else if (currentView === 'team-tasks') {
        loadAllTeamTasks();
      }
      // Also refresh dashboard data to update velocity metrics
      loadDashboardData();
      loadTopProjects();
      showNotification('success', `Task ${status === 'Completed' ? 'completed' : 'updated'} successfully!`);
    } catch (error) {
      console.error('Failed to update task:', error);
      showNotification('error', 'Failed to update task status');
    }
  };

  const createNewClient = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/clients`, newClientData);
      if (response.data.success) {
        setShowNewClientModal(false);
        setNewClientData({ name: '', company: '', contact_email: '', contact_phone: '', account_manager_id: '' });
        await loadDashboardData();

        // If creating from Move dialog, auto-select the new client
        if (showMoveProjectModal && response.data.id) {
          setMoveProjectData({
            ...moveProjectData,
            client_id: response.data.id.toString(),
            campaign_id: '' // Reset campaign when client changes
          });
          // Load campaigns for the new client
          await loadCampaignsForClient(response.data.id);
        }

        showNotification('success', 'Client created successfully!');
      }
    } catch (error) {
      console.error('Failed to create client:', error);
      showNotification('error', 'Failed to create client');
    }
  };

  const createNewCampaign = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/campaigns`, newCampaignData);
      if (response.data.success) {
        const campaignId = response.data.id;

        // Upload files if any
        if (campaignFiles.length > 0) {
          const formData = new FormData();
          formData.append('entity_type', 'campaign');
          formData.append('entity_id', campaignId.toString());
          campaignFiles.forEach(file => {
            formData.append('files', file);
          });

          try {
            await axios.post(`${API_URL}/api/uploads`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (uploadError) {
            console.error('Failed to upload files:', uploadError);
            showNotification('error', 'Campaign created but file upload failed');
          }
        }

        setShowNewCampaignModal(false);
        const campaignClientId = newCampaignData.client_id;
        setNewCampaignData({ client_id: '', name: '', description: '', budget: '', start_date: '', end_date: '', account_manager_id: '' });
        setCampaignFiles([]);

        if (selectedClient) {
          await loadCampaignsForClient(selectedClient.id);
        }

        // If creating from Move dialog, reload campaigns and auto-select
        if (showMoveProjectModal && campaignClientId) {
          await loadCampaignsForClient(parseInt(campaignClientId));
          if (campaignId) {
            setMoveProjectData({
              ...moveProjectData,
              campaign_id: campaignId.toString()
            });
          }
        }

        showNotification('success', 'Campaign created successfully!');
      }
    } catch (error) {
      console.error('Failed to create campaign:', error);
      showNotification('error', 'Failed to create campaign');
    }
  };

  const createNewProject = async () => {
    try {
      const projectData: any = { ...newProjectData };

      // Inherit account_manager_id from the selected campaign
      if (projectData.campaign_id) {
        const selectedCampaignData = allCampaigns.find(c => c.id === parseInt(projectData.campaign_id));
        if (selectedCampaignData && selectedCampaignData.account_manager_id) {
          projectData.account_manager_id = selectedCampaignData.account_manager_id;
        }
      }

      // If advanced project mode, add project type and phases
      if (projectCreationMode === 'advanced' && selectedProjectType) {
        projectData.project_type = selectedProjectType;
        projectData.phases = PROJECT_TEMPLATES[selectedProjectType] || [];

        // If Voyager project, add the level
        if (selectedProjectType === 'Voyager' && voyagerLevel) {
          projectData.voyager_level = voyagerLevel;
        }
      }

      const response = await axios.post(`${API_URL}/api/projects`, projectData);
      if (response.data.success) {
        const projectId = response.data.id;

        // Upload files if any
        if (projectFiles.length > 0) {
          const formData = new FormData();
          formData.append('entity_type', 'project');
          formData.append('entity_id', projectId.toString());
          projectFiles.forEach(file => {
            formData.append('files', file);
          });

          try {
            await axios.post(`${API_URL}/api/uploads`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (uploadError) {
            console.error('Failed to upload files:', uploadError);
            showNotification('error', 'Project created but file upload failed');
          }
        }

        setShowNewProjectModal(false);
        setNewProjectData({ campaign_id: '', name: '', description: '', project_manager: '', priority: 'Medium', account_manager_id: '' });
        setProjectFiles([]);
        setProjectCreationMode('quick');
        setSelectedProjectType('');
        setVoyagerLevel('');
        if (currentView === 'project-management') {
          loadProjectManagementData();
        } else if (currentView === 'campaign-detail' && selectedCampaign) {
          loadProjectsForCampaign(selectedCampaign.id);
        } else if (currentView === 'account-managers' || currentView === 'voyager') {
          loadAllProjects();
        }
        showNotification('success', 'Project created successfully!');
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      showNotification('error', 'Failed to create project');
    }
  };

  const createNewTask = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/tasks`, {
        ...newTaskData,
        assigned_by: currentUser,
        created_by: currentUser
      });
      if (response.data.success) {
        const taskId = response.data.id;

        // Upload files if any
        if (taskFiles.length > 0) {
          const formData = new FormData();
          formData.append('entity_type', 'task');
          formData.append('entity_id', taskId.toString());
          taskFiles.forEach(file => {
            formData.append('files', file);
          });

          try {
            await axios.post(`${API_URL}/api/uploads`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (uploadError) {
            console.error('Failed to upload files:', uploadError);
            showNotification('error', 'Task created but file upload failed');
          }
        }

        setShowNewTaskModal(false);
        setNewTaskData({ project_id: '', name: '', description: '', assigned_to: '', due_date: '', priority: 'Medium', phase: 'Strategy', estimated_hours: '' });
        setTaskFiles([]);
        if (currentView === 'technician') {
          loadTechnicianTasks();
        } else if (currentView === 'project-management') {
          loadProjectManagementData();
        } else if (currentView === 'project-detail' && selectedProject) {
          loadTasksForProject(selectedProject.id);
        } else if (currentView === 'priorities') {
          loadAllTeamTasks();
        }
        // Always refresh dashboard metrics when tasks change
        loadDashboardData();
        loadTopProjects();
        showNotification('success', 'Task created successfully!');
      }
    } catch (error) {
      console.error('Failed to create task:', error);
      showNotification('error', 'Failed to create task');
    }
  };

  const createNewPriority = async () => {
    try {
      const priorityData = {
        account_manager_id: selectedAccountManagerForPriority,
        ...newPriorityData
      };
      const response = await axios.post(`${API_URL}/api/account-manager-priorities`, priorityData);
      if (response.data.success) {
        setShowNewPriorityModal(false);
        setSelectedAccountManagerForPriority(null);
        setNewPriorityData({ title: '', description: '', due_date: '', priority: 'Medium', status: 'Not Started', resource: '' });
        // Reload account managers data
        loadAccountManagers();
        showNotification('success', 'Priority created successfully!');
      }
    } catch (error) {
      console.error('Failed to create priority:', error);
      showNotification('error', 'Failed to create priority');
    }
  };

  const updatePriority = async () => {
    if (!editingPriority) return;
    try {
      const response = await axios.put(`${API_URL}/api/account-manager-priorities/${editingPriority.id}`, {
        title: editingPriority.title,
        description: editingPriority.description,
        due_date: editingPriority.due_date,
        status: editingPriority.status,
        priority: editingPriority.priority,
        resource: editingPriority.resource
      });
      if (response.data.success) {
        setShowPriorityDetailModal(false);
        setSelectedPriority(null);
        setEditingPriority(null);
        loadAccountManagers();
        showNotification('success', 'Priority updated successfully!');
      }
    } catch (error) {
      console.error('Failed to update priority:', error);
      showNotification('error', 'Failed to update priority');
    }
  };

  const deletePriority = async (priorityId: number) => {
    if (!window.confirm('Are you sure you want to delete this priority?')) {
      return;
    }
    try {
      const response = await axios.delete(`${API_URL}/api/account-manager-priorities/${priorityId}`);
      if (response.data.success) {
        setShowPriorityDetailModal(false);
        setSelectedPriority(null);
        setEditingPriority(null);
        loadAccountManagers();
        showNotification('success', 'Priority deleted successfully!');
      }
    } catch (error) {
      console.error('Failed to delete priority:', error);
      showNotification('error', 'Failed to delete priority');
    }
  };

  // Edit functions
  const updateClient = async () => {
    if (!editingClient) return;
    try {
      console.log('🔍 DEBUG: editingClient state:', editingClient);

      // Send all editable fields to the backend
      const updateData = {
        name: editingClient.name,
        company: editingClient.company || editingClient.name,
        contact_email: editingClient.contact_email || '',
        contact_phone: editingClient.contact_phone || '',
        account_manager_id: editingClient.account_manager_id || null
      };

      console.log('🔍 DEBUG: updateData being sent:', updateData);
      console.log('🔍 DEBUG: API URL:', `${API_URL}/api/clients/${editingClient.id}`);

      const response = await axios.put(`${API_URL}/api/clients/${editingClient.id}`, updateData);

      console.log('🔍 DEBUG: API response:', response.data);

      if (response.data.success) {
        setShowEditClientModal(false);
        setEditingClient(null);
        loadDashboardData();
        showNotification('success', 'Client updated successfully!');
      }
    } catch (error) {
      console.error('❌ Failed to update client:', error);
      showNotification('error', 'Failed to update client');
    }
  };

  const updateCampaign = async () => {
    if (!editingCampaign) return;
    try {
      const response = await axios.put(`${API_URL}/api/campaigns/${editingCampaign.id}`, editingCampaign);
      if (response.data.success) {
        setShowEditCampaignModal(false);
        setEditingCampaign(null);
        if (selectedClient) {
          loadCampaignsForClient(selectedClient.id);
        }
        showNotification('success', 'Campaign updated successfully!');
      }
    } catch (error) {
      console.error('Failed to update campaign:', error);
      showNotification('error', 'Failed to update campaign');
    }
  };

  const updateProject = async () => {
    if (!editingProject) return;
    try {
      const response = await axios.put(`${API_URL}/api/projects/${editingProject.id}`, editingProject);
      if (response.data.success) {
        setShowEditProjectModal(false);
        setEditingProject(null);
        if (selectedCampaign) {
          loadProjectsForCampaign(selectedCampaign.id);
        }
        // Reload all projects to update Voyager view
        loadDashboardData();
        // showNotification('success', 'Project updated successfully!');
      }
    } catch (error) {
      console.error('Failed to update project:', error);
      showNotification('error', 'Failed to update project');
    }
  };

  const updateTask = async () => {
    if (!editingTask) return;
    try {
      const response = await axios.put(`${API_URL}/api/tasks/${editingTask.id}/full`, {
        ...editingTask,
        assigned_by: currentUser,
        updated_by: currentUser
      });
      if (response.data.success) {
        setShowEditTaskModal(false);
        setEditingTask(null);
        // Always reload all team tasks to update Team Priorities view
        await loadAllTeamTasks();
        // Reload tasks based on current view
        if (currentView === 'technician') {
          loadTechnicianTasks();
        } else if (currentView === 'team-tasks') {
          loadAllTeamTasks();
        }
        // Refresh project tasks if viewing a project
        if (selectedProject) {
          const projectResponse = await axios.get(`${API_URL}/api/projects/${selectedProject.id}/tasks`);
          setProjectTasks(projectResponse.data);
        }
        // Refresh dashboard to update velocity metrics
        loadDashboardData();
        loadTopProjects();
        showNotification('success', 'Task updated successfully!');
      }
    } catch (error) {
      console.error('Failed to update task:', error);
      showNotification('error', 'Failed to update task');
    }
  };

  // Delete functions
  const confirmDelete = (type: 'client' | 'campaign' | 'project' | 'task', id: number) => {
    setDeleteType(type);
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const executeDelete = async () => {
    if (!deleteType || !deleteId) return;

    try {
      let response;
      switch (deleteType) {
        case 'client':
          response = await axios.delete(`${API_URL}/api/clients/${deleteId}`);
          if (response.data.success) {
            loadDashboardData();
            if (selectedClient?.id === deleteId) {
              setSelectedClient(null);
              updateView('overview');
            }
          }
          break;
        case 'campaign':
          response = await axios.delete(`${API_URL}/api/campaigns/${deleteId}`);
          if (response.data.success && selectedClient) {
            loadCampaignsForClient(selectedClient.id);
          }
          break;
        case 'project':
          response = await axios.delete(`${API_URL}/api/projects/${deleteId}`);
          if (response.data.success) {
            loadDashboardData();
            loadTopProjects();
            // Navigate back to projects list
            setSelectedProject(null);
            updateView('projects');
          }
          break;
        case 'task':
          response = await axios.delete(`${API_URL}/api/tasks/${deleteId}`);
          if (response.data.success) {
            loadTechnicianTasks();
            // Refresh project tasks if viewing a project
            if (selectedProject) {
              const projectResponse = await axios.get(`${API_URL}/api/projects/${selectedProject.id}/tasks`);
              setProjectTasks(projectResponse.data);
            }
            // Refresh dashboard metrics
            loadDashboardData();
            loadTopProjects();
          }
          break;
      }

      setShowDeleteConfirm(false);
      setDeleteType(null);
      setDeleteId(null);
      showNotification('success', `${deleteType.charAt(0).toUpperCase() + deleteType.slice(1)} deleted successfully!`);
    } catch (error) {
      console.error(`Failed to delete ${deleteType}:`, error);
      showNotification('error', `Failed to delete ${deleteType}`);
    }
  };

  const handleClientClick = (client: Client) => {
    setSelectedClient(client);
    loadCampaignsForClient(client.id);
    updateView('client-detail');
  };

  // Bulk task import functions
  const parseBulkTasks = (text: string) => {
    const lines = text.split('\n').filter(line => line.trim());
    const today = new Date();
    const defaultDueDate = new Date(today.setDate(today.getDate() + 7)).toISOString().split('T')[0];

    return lines.map((line, index) => {
      const parts = line.split('|').map(p => p.trim());

      if (parts.length === 1) {
        // Simple format: just task name
        return {
          name: parts[0],
          description: '',
          priority: 'Medium',
          phase: 'Strategy',
          assigned_to: 'Unassigned',
          due_date: defaultDueDate,
          estimated_hours: 0
        };
      } else {
        // Structured format: Task Name | Priority | Phase | Assignee | Due Date | Hours
        return {
          name: parts[0] || `Task ${index + 1}`,
          description: '',
          priority: parts[1] || 'Medium',
          phase: parts[2] || 'Strategy',
          assigned_to: parts[3] || 'Unassigned',
          due_date: parts[4] || defaultDueDate,
          estimated_hours: parseFloat(parts[5]) || 0
        };
      }
    });
  };

  const handleBulkTaskTextChange = (text: string) => {
    setBulkTaskText(text);
    if (text.trim()) {
      const preview = parseBulkTasks(text);
      setBulkTaskPreview(preview);
    } else {
      setBulkTaskPreview([]);
    }
  };

  const handleBulkTaskImport = async () => {
    if (!selectedProject || bulkTaskPreview.length === 0) return;

    try {
      const tasksToCreate = bulkTaskPreview.map(task => ({
        ...task,
        project_id: selectedProject.id.toString(),
        status: 'Not Started'
      }));

      // Create tasks one by one
      for (const task of tasksToCreate) {
        await axios.post(`${API_URL}/api/tasks`, task);
      }

      showNotification('success', `Successfully imported ${tasksToCreate.length} tasks!`);
      setShowBulkTaskModal(false);
      setBulkTaskText('');
      setBulkTaskPreview([]);

      // Refresh project data
      if (selectedProject) {
        const response = await axios.get(`${API_URL}/api/projects/${selectedProject.id}/tasks`);
        setProjectTasks(response.data);
      }
      loadDashboardData();
      loadTopProjects();
    } catch (error) {
      console.error('Failed to import tasks:', error);
      showNotification('error', 'Failed to import tasks');
    }
  };

  // const getHealthScoreColor = (score: number) => {
  //   if (score >= 8) return 'text-green-600';
  //   if (score >= 6) return 'text-yellow-600';
  //   return 'text-red-600';
  // }; // Removed - unused

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'Not Started': 'badge-status-not-started',
      'In Progress': 'badge-status-in-progress',
      'Ready for Review': 'badge-status-ready-for-review',
      'On Hold': 'badge-status-on-hold',
      'Completed': 'badge-status-completed',
      'Cancelled': 'badge-status-cancelled',
      // Legacy status values (fallback)
      'Active': 'badge-status-in-progress',
      'Planning': 'badge-status-not-started',
      'Client Review': 'badge-status-in-progress',
      'Discovery': 'badge-status-not-started',
      'Blocked': 'badge-status-on-hold'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: string) => {
    const colors: { [key: string]: string } = {
      'Critical': 'badge-priority-critical',
      'High': 'badge-priority-high',
      'Medium': 'badge-priority-medium',
      'Low': 'badge-priority-low'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getPhaseColor = (phase: string) => {
    // Strategy phases
    if (phase?.startsWith('Strategy')) {
      return 'badge-phase-strategy';
    }
    // Fulfillment phases
    if (phase?.startsWith('Fulfillment')) {
      return 'badge-phase-fulfillment';
    }
    // Insights & Innovation
    if (phase === 'Insights & Innovation') {
      return 'badge-phase-insights';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'N/A';
    }
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getAssignedToName = (assignedTo: string | number): string => {
    if (!assignedTo) return 'Unassigned';

    // If it's already a string name (not just a number), return it
    const assignedToStr = String(assignedTo);
    if (isNaN(Number(assignedToStr)) || assignedToStr.includes(' ')) {
      return assignedToStr;
    }

    // If it's a number or numeric string, try to find the team member by ID
    const memberId = Number(assignedToStr);
    const member: any = teamMembers.find((m: any) => m.id === memberId);
    return member ? (member.name as string) : `User #${assignedToStr}`;
  };

  const renderOverview = () => (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <button
          onClick={() => setShowNewClientModal(true)}
          className="btn-success"
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#157347'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#198754'}
        >
          + New Client
        </button>
      </div>

      {/* Key Metrics */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-content">
              <p className="stat-label">Active Clients</p>
              <p className="stat-value">{dashboardData.clients.length}</p>
            </div>
            <UserGroupIcon className="text-blue-600 icon-24" />
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-content">
              <p className="stat-label">Active Campaigns</p>
              <p className="stat-value">
                {dashboardData.clients.reduce((sum, client) => sum + client.active_campaigns, 0)}
              </p>
            </div>
            <BriefcaseIcon className="text-green-600 icon-24" />
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-content">
              <p className="stat-label">Total Projects</p>
              <p className="stat-value">
                {dashboardData.clients.reduce((sum, client) => sum + client.total_projects, 0)}
              </p>
            </div>
            <ClipboardDocumentListIcon className="text-purple-600 icon-24" />
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-content">
              <p className="stat-label">Total Tasks</p>
              <p className="stat-value">
                {dashboardData.clients.reduce((sum, client) => sum + client.total_tasks, 0)}
              </p>
            </div>
            <CheckCircleIcon className="text-orange-600 icon-24" />
          </div>
        </div>
      </div>


      {/* Project Velocity Chart and Recent Completions */}
      <div className="mb-6" style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '24px' }}>
        {/* Top 10 Projects */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Top 10 Projects by Tasks Completed</h2>
          <div className="bg-white rounded-lg shadow-sm border p-6">
          {topProjects.length > 0 ? (
            <div className="flex-column-gap-12">
              {topProjects.slice(0, 10).map((project, index) => {
                // Generate realistic sparkline data based on actual completion
                const generateRealisticData = (completed: number, total: number, seed: number) => {
                  const data = [];
                  // const completionRatio = total > 0 ? completed / total : 0; // Removed - unused

                  // If no tasks completed, show flat/near-zero line
                  if (completed === 0) {
                    return Array(7).fill(0);
                  }

                  // Generate a progression that builds up to current completion
                  let value = 0;
                  const increment = completed / 7;

                  for (let i = 0; i < 7; i++) {
                    // Add some variance based on seed for uniqueness
                    const variance = ((seed * (i + 1) * 1103515245) % 100) / 100;
                    const dayValue = Math.floor(value + variance * increment * 0.3);
                    data.push(Math.min(dayValue, completed));
                    value += increment;
                  }

                  // Ensure last value reflects actual completion
                  data[6] = completed;

                  return data;
                };

                const sparklineData = generateRealisticData(
                  project.completed_tasks || 0,
                  project.total_tasks || 0,
                  project.id
                );
                const maxValue = Math.max(...sparklineData, 1);

                return (
                  <div
                    key={project.id}
                    className="flex-center"
                    style={{
                      marginBottom: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={async () => {
                      // Navigate to project detail view
                      console.log('Clicking project:', project);
                      try {
                        // Find the client by name
                        const client = dashboardData.clients.find((cl: Client) => cl.name === project.client_name);
                        console.log('Found client:', client);

                        if (!client) {
                          console.error('Client not found for:', project.client_name);
                          showNotification('error', 'Client not found');
                          return;
                        }

                        // Load campaigns for this client
                        console.log('Fetching campaigns for client:', client.id);
                        const campaignsResponse = await axios.get(`${API_URL}/api/clients/${client.id}/campaigns`);
                        console.log('Campaigns response:', campaignsResponse.data);
                        const campaign = campaignsResponse.data.find((c: Campaign) => c.name === project.campaign_name);
                        console.log('Found campaign:', campaign);

                        if (!campaign) {
                          console.error('Campaign not found for:', project.campaign_name);
                          showNotification('error', 'Campaign not found');
                          return;
                        }

                        // Load projects for this campaign
                        console.log('Fetching projects for campaign:', campaign.id);
                        const projectsResponse = await axios.get(`${API_URL}/api/campaigns/${campaign.id}/projects`);
                        console.log('Projects response:', projectsResponse.data);
                        const fullProject = projectsResponse.data.find((p: Project) => p.id === project.id);
                        console.log('Found full project:', fullProject);

                        if (!fullProject) {
                          console.error('Project not found with ID:', project.id);
                          showNotification('error', 'Project not found');
                          return;
                        }

                        // Load project tasks
                        console.log('Fetching tasks for project:', project.id);
                        const tasksResponse = await axios.get(`${API_URL}/api/projects/${project.id}/tasks`);
                        console.log('Tasks response:', tasksResponse.data);
                        setProjectTasks(tasksResponse.data);

                        // Set states to navigate to project view
                        console.log('Setting view states...');
                        setSelectedClient(client);
                        setSelectedCampaign(campaign);
                        setSelectedProject(fullProject);
                        updateView('project-detail');
                        console.log('Navigation complete');
                      } catch (error) {
                        console.error('Failed to navigate to project:', error);
                        showNotification('error', 'Failed to open project');
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <div className="project-rank">
                      {index + 1}.
                    </div>
                    <div className="flex-1">
                      <div className="flex-between" style={{ marginBottom: '8px' }}>
                        <span className="project-name">
                          {project.name}
                        </span>
                        <span className="project-velocity">
                          {project.completed_tasks || 0} / {project.total_tasks || 0} tasks
                        </span>
                      </div>

                      {/* Mini Sparkline Chart */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <svg width="120" height="32" style={{ overflow: 'visible' }}>
                          {/* Grid lines */}
                          <line x1="0" y1="16" x2="120" y2="16" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="2,2" />

                          {/* Smooth Sparkline path using cubic bezier curves */}
                          <path
                            d={(() => {
                              const points = sparklineData.map((value, i) => ({
                                x: i * 20,
                                y: 32 - (value / maxValue) * 28
                              }));

                              if (points.length === 0) return '';

                              let path = `M ${points[0].x} ${points[0].y}`;

                              for (let i = 0; i < points.length - 1; i++) {
                                const current = points[i];
                                const next = points[i + 1];
                                const controlPointX = (current.x + next.x) / 2;

                                path += ` Q ${controlPointX} ${current.y}, ${controlPointX} ${(current.y + next.y) / 2}`;
                                path += ` Q ${controlPointX} ${next.y}, ${next.x} ${next.y}`;
                              }

                              return path;
                            })()}
                            fill="none"
                            stroke={index < 3 ? '#10b981' : index < 6 ? '#3b82f6' : '#8b5cf6'}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeDasharray="200"
                            strokeDashoffset="200"
                            style={{
                              animation: `drawLine 1.2s ease-out forwards`,
                              animationDelay: `${index * 0.1}s`
                            }}
                          />

                          {/* Data points */}
                          {sparklineData.map((value, i) => (
                            <circle
                              key={i}
                              cx={i * 20}
                              cy={32 - (value / maxValue) * 28}
                              r="3"
                              fill={index < 3 ? '#10b981' : index < 6 ? '#3b82f6' : '#8b5cf6'}
                              stroke="white"
                              strokeWidth="1.5"
                              style={{
                                opacity: 0,
                                animation: `fadeIn 0.3s ease-out forwards`,
                                animationDelay: `${index * 0.1 + 1}s`
                              }}
                            />
                          ))}
                        </svg>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '13px',
                          color: '#6b7280'
                        }}>
                          <span>7-day trend</span>
                          <span style={{
                            fontWeight: '600',
                            color: index < 3 ? '#10b981' : index < 6 ? '#3b82f6' : '#8b5cf6'
                          }}>
                            {project.velocity_percentage || 0}% complete
                          </span>
                        </div>
                      </div>

                      <div className="velocity-meta">
                        {project.client_name} → {project.campaign_name}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No project data available</p>
          )}
          </div>
        </div>

        {/* Recent Completions / In Progress */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">
              {showInProgress ? 'In Progress' : 'Recent Completions'}
            </h2>
            <button
              onClick={() => setShowInProgress(!showInProgress)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '500',
                color: '#2563eb',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dbeafe';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
              }}
            >
              {showInProgress ? 'Show Completed' : 'Show In Progress'}
            </button>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            {showInProgress ? (
              // Show In Progress tasks
              tasks.filter(task => task.status === 'In Progress').length > 0 ? (
                <div className="space-y-4">
                  {tasks
                    .filter(task => task.status === 'In Progress')
                    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
                    .slice(0, 10)
                    .map((task, index) => (
                      <div
                        key={index}
                        className="pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                      >
                        <div className="flex items-center gap-2">
                          <div style={{ width: '16px', height: '16px', color: '#3b82f6', flexShrink: 0, marginRight: '4px' }}>
                            <svg fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className="text-sm font-medium text-gray-900 truncate flex-1">
                            {task.name}
                          </p>
                        </div>
                        <p className="text-xs text-gray-500 mt-1" style={{ wordBreak: 'break-word' }}>
                          by {getAssignedToName(task.assigned_to)}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5" style={{ wordBreak: 'break-word' }}>
                          {task.client_name} → {task.project_name}
                        </p>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8 text-sm">No tasks in progress</p>
              )
            ) : (
              // Show Recent Completions
              recentCompletions.length > 0 ? (
                <div className="space-y-4">
                  {recentCompletions.map((completion, index) => (
                    <div
                      key={index}
                      className="pb-4 border-b border-gray-100 last:border-0 last:pb-0"
                    >
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon style={{ width: '16px', height: '16px', color: '#10b981', flexShrink: 0, marginRight: '4px' }} />
                        <p className="text-sm font-medium text-gray-900 truncate flex-1">
                          {completion.task_name}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" style={{ wordBreak: 'break-word' }}>
                        by {getAssignedToName(completion.assigned_to)}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5" style={{ wordBreak: 'break-word' }}>
                        {completion.client_name} → {completion.project_name}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8 text-sm">No recent completions</p>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderClientDetail = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => updateView('overview')}
            className="btn-outline-primary"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0d6efd';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#0d6efd';
            }}
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{selectedClient?.name}</h1>
        </div>
      </div>

      {/* Campaigns for this client */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Campaigns</h2>
          <button
            onClick={() => {
              setNewCampaignData({...newCampaignData, client_id: selectedClient?.id.toString() || ''});
              setShowNewCampaignModal(true);
            }}
            style={{
              padding: '6px 12px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              backgroundColor: '#0d6efd',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            + New Campaign
          </button>
        </div>
        <div className="grid gap-4">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer p-5"
              onClick={() => handleCampaignClick(campaign)}
            >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{campaign.name}</h3>
                    <p className="text-gray-600 mb-3">{campaign.description}</p>

                    <div className="flex items-center space-x-6 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.current_phase || campaign.status)}`}>
                        {campaign.current_phase || campaign.status}
                      </span>
                      <span className="text-gray-500">
                        {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}
                      </span>
                      <span className="text-gray-500">
                        Budget: {formatCurrency(campaign.budget)}
                      </span>
                      <span className="text-gray-500">
                        {campaign.project_count} projects
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 ml-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-500 mb-1">Progress</div>
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${campaign.progress_percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {campaign.progress_percentage}%
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCampaign(campaign);
                          setShowEditCampaignModal(true);
                        }}
                        className="icon-btn icon-btn-edit"
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="Edit campaign"
                      >
                        <PencilIcon className="icon-16" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          confirmDelete('campaign', campaign.id);
                        }}
                        className="icon-btn icon-btn-delete"
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="Delete campaign"
                      >
                        <TrashIcon className="icon-16" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );

  const renderAccountManagement = () => {
    const filteredClients = dashboardData.clients.filter(client =>
      client.name?.toLowerCase().includes(clientSearchQuery.toLowerCase())
    );

    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Account Management</h1>
          <button
            onClick={() => setShowNewClientModal(true)}
            className="btn-success"
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#157347'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#198754'}
          >
            + New Client
          </button>
        </div>

        {/* Client Velocity Overview */}
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Client Overview</h2>

          <div className="mb-4" style={{ marginBottom: '1.5rem' }}>
            <input
              type="text"
              placeholder="Search by client name..."
              value={clientSearchQuery}
              onChange={(e) => setClientSearchQuery(e.target.value)}
              className="form-control"
              style={{
                width: '200px',
                padding: '0.375rem 0.75rem',
                fontSize: '1rem',
                lineHeight: '1.5',
                color: '#212529',
                backgroundColor: '#fff',
                border: '1px solid #ced4da',
                borderRadius: '0.25rem',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#86b7fe';
                e.currentTarget.style.boxShadow = '0 0 0 0.25rem rgba(13, 110, 253, 0.25)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#ced4da';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            {clientSearchQuery && (
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.5rem', color: '#6c757d' }}>
                Showing {filteredClients.length} of {dashboardData.clients.length} clients
              </p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            {filteredClients.length === 0 && dashboardData.clients.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6 text-center" style={{ gridColumn: '1 / -1' }}>
                <p className="text-gray-500">No clients found. Create your first client!</p>
              </div>
            )}
            {filteredClients.length === 0 && dashboardData.clients.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6 text-center" style={{ gridColumn: '1 / -1' }}>
                <p className="text-gray-500">No clients match "{clientSearchQuery}"</p>
              </div>
            )}
            {filteredClients
              .sort((a, b) => {
                // Sort alphabetically by client name
                return a.name.localeCompare(b.name);
              })
              .map((client) => (
            <div
              key={client.id}
              className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow"
              style={{
                borderColor: '#e5e7eb',
                padding: '20px'
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 cursor-pointer" onClick={() => handleClientClick(client)}>
                  <div className="flex-shrink-0 bg-gray-100 p-3 rounded-lg">
                    <BuildingOfficeIcon className="h-8 w-8 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{client.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">View details and campaigns</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-3 px-4 py-2 bg-gray-50 rounded-lg">
                    <div
                      className="text-center cursor-pointer hover:bg-blue-50 px-1 py-1 rounded transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClientClick(client);
                      }}
                      title="View campaigns"
                    >
                      <p className="text-xl font-bold text-blue-600">{client.active_campaigns}</p>
                      <p className="text-xs text-gray-600 font-medium uppercase tracking-wide">Campaigns</p>
                    </div>
                    <div className="w-px h-10 bg-gray-300"></div>
                    <div
                      className="text-center cursor-pointer hover:bg-purple-50 px-1 py-1 rounded transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClientClick(client);
                      }}
                      title="View projects"
                    >
                      <p className="text-xl font-bold text-purple-600">{client.total_projects}</p>
                      <p className="text-xs text-gray-600 font-medium uppercase tracking-wide">Projects</p>
                    </div>
                    <div className="w-px h-10 bg-gray-300"></div>
                    <div
                      className="text-center cursor-pointer hover:bg-orange-50 px-1 py-1 rounded transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClientClick(client);
                      }}
                      title="View tasks"
                    >
                      <p className="text-xl font-bold text-orange-600">{client.total_tasks}</p>
                      <p className="text-xs text-gray-600 font-medium uppercase tracking-wide">Tasks</p>
                    </div>
                    <div className="w-px h-10 bg-gray-300"></div>
                    <div className="text-center">
                      <p className="text-xl font-bold text-green-600">{Math.round(client.avg_campaign_progress || 0)}%</p>
                      <p className="text-xs text-gray-600 font-medium uppercase tracking-wide">Progress</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingClient(client);
                        setShowEditClientModal(true);
                      }}
                      className="icon-btn icon-btn-edit icon-btn-lg"
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Edit client"
                    >
                      <PencilIcon className="icon-20" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete('client', client.id);
                      }}
                      className="icon-btn icon-btn-delete icon-btn-lg"
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Delete client"
                    >
                      <TrashIcon className="icon-20" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    );
  };

  const renderProjectManagement = () => {
    const filteredProjects = projects.filter(project =>
      project.name?.toLowerCase().includes(projectSearchQuery.toLowerCase()) ||
      project.client_name?.toLowerCase().includes(projectSearchQuery.toLowerCase()) ||
      project.campaign_name?.toLowerCase().includes(projectSearchQuery.toLowerCase())
    );

    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">All Projects</h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => {
                loadAllCampaigns();
                setShowNewProjectModal(true);
              }}
              className="btn-primary-green"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
            >
              + New Project
            </button>
            <button
              onClick={() => {
                loadAllProjects();
                setShowNewTaskModal(true);
              }}
              className="btn-primary"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0b5ed7'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0d6efd'}
            >
              + New Task
            </button>
          </div>
        </div>

        <div className="mb-4" style={{ marginBottom: '1.5rem' }}>
          <input
            type="text"
            placeholder="Search by project name, client, or campaign..."
            value={projectSearchQuery}
            onChange={(e) => setProjectSearchQuery(e.target.value)}
            className="form-control"
            style={{
              width: '200px',
              padding: '0.375rem 0.75rem',
              fontSize: '1rem',
              lineHeight: '1.5',
              color: '#212529',
              backgroundColor: '#fff',
              border: '1px solid #ced4da',
              borderRadius: '0.25rem',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#86b7fe';
              e.currentTarget.style.boxShadow = '0 0 0 0.25rem rgba(13, 110, 253, 0.25)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#ced4da';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          {projectSearchQuery && (
            <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.5rem', color: '#6c757d' }}>
              Showing {filteredProjects.length} of {projects.length} projects
            </p>
          )}
        </div>

        <div className="grid gap-4">
          {filteredProjects.length === 0 && projects.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
              <p className="text-gray-500">No projects found. Create your first project!</p>
            </div>
          )}
          {filteredProjects.length === 0 && projects.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
              <p className="text-gray-500">No projects match "{projectSearchQuery}"</p>
            </div>
          )}
          {filteredProjects.map((project) => {
            // Check if project has no account_manager_id assigned (highlighting needed)
            const hasNoAccountManager = !project.account_manager_id;

            return (
            <div
              key={project.id}
              className="rounded-lg shadow-sm border hover:shadow-md transition-shadow p-6 cursor-pointer"
              style={{ backgroundColor: hasNoAccountManager ? '#fff7ed' : 'white' }}
              onClick={() => handleProjectClick(project)}
            >
              <p className="text-sm text-gray-500 mb-2">{project.client_name} → {project.campaign_name}</p>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-900">{project.name}</h3>
                    <span className={`text-sm font-medium ${getPriorityColor(project.priority)}`}>
                      {project.priority}
                    </span>
                  </div>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-6 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                  <span className="text-gray-500">
                    AM: {project.project_manager || project.account_manager_name || 'Unassigned'}
                  </span>
                  <span className="text-gray-500">
                    Tasks: {project.completed_tasks}/{project.task_count}
                  </span>
                  {project.blocked_tasks > 0 && (
                    <span className="text-red-600 font-medium">
                      {project.blocked_tasks} blocked
                    </span>
                  )}
                </div>

                <div className="flex items-center">
                  <div className="text-sm text-gray-500 mr-2">Progress</div>
                  <div className="flex items-center">
                    <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${project.progress_percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {project.progress_percentage}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTechnicianView = () => (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Tasks</h1>

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Task Queue</h2>
        </div>
        <div className="p-6">
          <div className="grid gap-4">
            {tasks.map((task) => (
              <div key={task.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">{task.name}</h3>
                      <span className={`text-sm font-medium ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>

                    <p className="text-gray-600 mb-3">{task.description}</p>

                    <div className="flex items-center space-x-6 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                        {task.status}
                      </span>
                      <span className="text-gray-500">
                        Due: {formatDate(task.due_date)}
                      </span>
                      <span className="text-gray-500">
                        {task.client_name} → {task.project_name}
                      </span>
                      <span className="text-gray-500">
                        Est: {task.estimated_hours}h | Actual: {task.actual_hours}h
                      </span>
                    </div>
                  </div>

                  <div className="ml-4 flex space-x-2">
                    {task.status !== 'Completed' && (
                      <>
                        <button
                          onClick={() => updateTaskStatus(task.id, 'In Progress', task.actual_hours)}
                          style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'white',
                            backgroundColor: '#0d6efd',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '8px',
                            transition: 'background-color 0.15s ease-in-out'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0b5ed7'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0d6efd'}
                        >
                          Start
                        </button>
                        <button
                          onClick={() => updateTaskStatus(task.id, 'Completed', task.estimated_hours)}
                          style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: 'white',
                            backgroundColor: '#198754',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease-in-out'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#157347'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#198754'}
                        >
                          Complete
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        setEditingTask(task);
                        setShowEditTaskModal(true);
                      }}
                      style={{
                        padding: '6px',
                        color: '#2563eb',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Edit task"
                    >
                      <PencilIcon style={{ width: '16px', height: '16px' }} />
                    </button>
                    <button
                      onClick={() => confirmDelete('task', task.id)}
                      style={{
                        padding: '6px',
                        color: '#dc2626',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Delete task"
                    >
                      <TrashIcon style={{ width: '16px', height: '16px' }} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderCampaignsView = () => {
    const filteredCampaigns = allCampaigns.filter(campaign =>
      campaign.client_name?.toLowerCase().includes(campaignSearchQuery.toLowerCase())
    );

    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">All Campaigns</h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => {
                loadAllCampaigns();
                setShowNewProjectModal(true);
              }}
              className="btn-primary-green"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
            >
              + New Project
            </button>
            <button
              onClick={() => setShowNewCampaignModal(true)}
              className="btn-primary"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0b5ed7'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0d6efd'}
            >
              + New Campaign
            </button>
          </div>
        </div>

        <div className="mb-4" style={{ marginBottom: '1.5rem' }}>
          <input
            type="text"
            placeholder="Search by client name..."
            value={campaignSearchQuery}
            onChange={(e) => setCampaignSearchQuery(e.target.value)}
            className="form-control"
            style={{
              width: '200px',
              padding: '0.375rem 0.75rem',
              fontSize: '1rem',
              lineHeight: '1.5',
              color: '#212529',
              backgroundColor: '#fff',
              border: '1px solid #ced4da',
              borderRadius: '0.25rem',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#86b7fe';
              e.currentTarget.style.boxShadow = '0 0 0 0.25rem rgba(13, 110, 253, 0.25)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#ced4da';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          {campaignSearchQuery && (
            <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.5rem', color: '#6c757d' }}>
              Showing {filteredCampaigns.length} of {allCampaigns.length} campaigns
            </p>
          )}
        </div>

        <div className="grid gap-4">
          {filteredCampaigns.length === 0 && allCampaigns.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
              <p className="text-gray-500">No campaigns found. Create your first campaign!</p>
            </div>
          )}
          {filteredCampaigns.length === 0 && allCampaigns.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
              <p className="text-gray-500">No campaigns match "{campaignSearchQuery}"</p>
            </div>
          )}
          {filteredCampaigns.map((campaign) => (
          <div
            key={campaign.id}
            className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow p-6 cursor-pointer"
            onClick={() => handleCampaignClick(campaign)}
          >
            <p className="text-sm text-gray-500 mb-2">Client: {campaign.client_name || 'Unknown'}</p>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-1">{campaign.name}</h3>
                <p className="text-gray-600">{campaign.description}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingCampaign(campaign);
                    setShowEditCampaignModal(true);
                  }}
                  style={{
                    padding: '6px',
                    color: '#2563eb',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  title="Edit campaign"
                >
                  <PencilIcon style={{ width: '16px', height: '16px' }} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    confirmDelete('campaign', campaign.id)
                  }}
                  style={{
                    padding: '6px',
                    color: '#dc2626',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  title="Delete campaign"
                >
                  <TrashIcon style={{ width: '16px', height: '16px' }} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-6 text-sm">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.current_phase || campaign.status)}`}>
                  {campaign.current_phase || campaign.status}
                </span>
                <span className="text-gray-500">
                  {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}
                </span>
                <span className="text-gray-500">
                  Budget: {formatCurrency(campaign.budget)}
                </span>
                <span className="text-gray-500">
                  {campaign.project_count || 0} projects
                </span>
              </div>

              <div className="flex items-center">
                <div className="text-sm text-gray-500 mr-2">Progress</div>
                <div className="flex items-center">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${campaign.progress_percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {campaign.progress_percentage}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    );
  };

  const renderProjectDetail = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => updateView('campaign-detail')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#0d6efd',
              backgroundColor: 'transparent',
              border: '1px solid #0d6efd',
              borderRadius: '6px',
              cursor: 'pointer',
              marginBottom: '8px',
              transition: 'all 0.15s ease-in-out'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0d6efd';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#0d6efd';
            }}
          >
            ← Back to Campaign
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {selectedProject?.client_name || selectedCampaign?.client_name}: {selectedProject?.name}
          </h1>
          <p className="text-gray-600 mt-1">{selectedProject?.description}</p>
          <div className="flex items-center space-x-4 mt-2 text-sm">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedProject?.status || '')}`}>
              {selectedProject?.status}
            </span>
            <span className="text-gray-500">
              AM: {selectedProject?.account_manager_name || selectedProject?.project_manager || 'Unassigned'}
            </span>
            <span className="text-gray-500">
              Priority: {selectedProject?.priority}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => {
              if (selectedProject && window.confirm(`Are you sure you want to delete the project "${selectedProject.name}"? This will also delete all associated tasks and cannot be undone.`)) {
                confirmDelete('project', selectedProject.id);
              }
            }}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#dc2626',
              backgroundColor: 'transparent',
              border: '1px solid #dc2626',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.15s ease-in-out'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#dc2626';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#dc2626';
            }}
          >
            🗑️ Delete Project
          </button>
          <button
            onClick={() => {
              console.log('Bulk Add button clicked!');
              console.log('showBulkTaskModal before:', showBulkTaskModal);
              setShowBulkTaskModal(true);
              console.log('setShowBulkTaskModal called');
            }}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#10b981',
              backgroundColor: 'transparent',
              border: '1px solid #10b981',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.15s ease-in-out'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#10b981';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#10b981';
            }}
          >
            📋 Bulk Add
          </button>
          <button
            onClick={() => {
              loadAllProjects();
              setNewTaskData({...newTaskData, project_id: selectedProject?.id.toString() || ''});
              setShowNewTaskModal(true);
            }}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              backgroundColor: '#0d6efd',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background-color 0.15s ease-in-out'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0b5ed7'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0d6efd'}
          >
            + New Task
          </button>
        </div>
      </div>

      {/* Project Phases Section - Only show if project has phases and not Voyager */}
      {projectPhases && projectPhases.length > 0 && projectPhases[0]?.project_type !== 'Voyager' && (
        <>
          {/* Phase Progress Bar */}
          <div className="bg-white rounded-lg shadow-sm border mb-6 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Project Phase Timeline</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center">
                {projectPhases.map((phase, index) => (
                  <div key={phase.id} className="flex items-center" style={{ flex: 1 }}>
                    {/* Phase Box */}
                    <div className="flex-1">
                      <div
                        className="relative px-4 py-3 rounded-lg text-center transition-all cursor-pointer"
                        style={{
                          backgroundColor: phase.status === 'completed' ? '#10b981' :
                                          phase.status === 'active' ? '#3b82f6' :
                                          '#e5e7eb',
                          color: phase.status === 'pending' ? '#6b7280' : 'white',
                          fontWeight: phase.status === 'active' ? '600' : '500',
                          fontSize: '14px',
                          opacity: selectedPhaseId === phase.id ? 1 : 0.7,
                          border: selectedPhaseId === phase.id ? '2px solid #1e40af' : '2px solid transparent'
                        }}
                        onClick={() => setSelectedPhaseId(phase.id)}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = selectedPhaseId === phase.id ? '1' : '0.7'}
                      >
                        <div className="font-semibold">
                          {phase.name.replace(/^Phase \d+:\s*/, '').replace('Insights & Innovation', 'I&I')}
                        </div>
                        <div className="text-xs opacity-90 mt-1">{phase.duration}</div>
                      </div>
                    </div>

                    {/* Arrow between phases */}
                    {index < projectPhases.length - 1 && (
                      <div className="flex-shrink-0 mx-2">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M9 5l7 7-7 7" stroke={phase.status === 'completed' ? '#10b981' : '#d1d5db'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Phase Details - Show Only Selected Phase */}
          {(() => {
            const selectedPhase = projectPhases.find(p => p.id === selectedPhaseId);
            if (!selectedPhase) return null;

            const phaseIndex = projectPhases.findIndex(p => p.id === selectedPhaseId);
            const allCriteriaCompleted = selectedPhase.criteria && selectedPhase.criteria.length > 0 &&
              selectedPhase.criteria.every((c: any) => c.is_completed);
            const isActivePhase = selectedPhase.status === 'active';
            const isVoyagerProject = selectedPhase.project_type === 'Voyager';
            const hasActivities = !isVoyagerProject && selectedPhase.activities && selectedPhase.activities.length > 0;
            const hasDeliverables = !isVoyagerProject && selectedPhase.deliverables && selectedPhase.deliverables.length > 0;
            const hasCriteria = !isVoyagerProject && selectedPhase.criteria && selectedPhase.criteria.length > 0;

            return (
              <div className="bg-white rounded-lg shadow-sm border mb-6">
                <div className="px-6 py-4 border-b border-gray-200" style={{
                  backgroundColor: isActivePhase ? '#eff6ff' : 'white'
                }}>
                  <h2 className="text-lg font-medium text-gray-900">{selectedPhase.name}</h2>
                  <div className="flex gap-4 text-sm mt-2">
                    <div>
                      <span className="font-medium text-gray-700">Owner:</span> <span className="text-gray-600">{selectedPhase.owner}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Duration:</span> <span className="text-gray-600">{selectedPhase.duration}</span>
                    </div>
                  </div>
                </div>
                <div className="p-6 flex gap-6">
                  {/* Left side: Activities and Deliverables (2/3) */}
                  <div className="flex-1" style={{ flex: '2' }}>
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Activities */}
                      {hasActivities && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">Activities</span>
                          </h4>
                          <ul className="space-y-2">
                            {selectedPhase.activities.map((activity: string, idx: number) => {
                              const isDiscoveryActivity = activity.toLowerCase().includes('discovery') && activity.toLowerCase().includes('interview');
                              return (
                                <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                                  <span className="text-blue-500 flex-shrink-0" style={{ lineHeight: '1.5' }}>•</span>
                                  {isDiscoveryActivity ? (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        const projectType = selectedPhase.project_type;
                                        const clientId = selectedPhase.client_id;
                                        console.log('Discovery clicked', {
                                          clientId: clientId,
                                          projectType: projectType,
                                          selectedPhase: selectedPhase
                                        });
                                        if (clientId && projectType) {
                                          setDiscoveryClientId(clientId);
                                          setDiscoveryProjectType(projectType);
                                          setShowDiscoveryForm(true);
                                        } else {
                                          alert(`Missing data - Client ID: ${clientId || 'none'}, Project Type: ${projectType || 'none'}`);
                                        }
                                      }}
                                      style={{
                                        lineHeight: '1.5',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        font: 'inherit',
                                        color: '#1d4ed8',
                                        textDecoration: 'underline',
                                        cursor: 'pointer'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.color = '#1e40af'}
                                      onMouseLeave={(e) => e.currentTarget.style.color = '#1d4ed8'}
                                    >
                                      {activity}
                                    </button>
                                  ) : (
                                    <span style={{ lineHeight: '1.5' }}>{activity}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Deliverables */}
                      {hasDeliverables && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">Deliverables</span>
                          </h4>
                          <ul className="space-y-2">
                            {selectedPhase.deliverables.map((deliverable: string, idx: number) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                                <span className="text-purple-500 flex-shrink-0" style={{ lineHeight: '1.5' }}>•</span>
                                <span style={{ lineHeight: '1.5' }}>{deliverable}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right sidebar: Criteria to Proceed (1/3) */}
                  {hasCriteria && (
                    <div style={{ flex: '1', minWidth: '300px' }} className="border-l pl-6">
                      <div className="sticky top-6">
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Criteria to Proceed</span>
                        </h4>
                        <div className="space-y-3">
                          {selectedPhase.criteria.map((criteria: any) => (
                            <label
                              key={criteria.id}
                              className={`flex items-start gap-3 p-3 rounded-lg transition ${
                                isActivePhase ? 'cursor-pointer hover:bg-green-50' : 'cursor-not-allowed opacity-60'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={criteria.is_completed}
                                onChange={(e) => isActivePhase && handleCriteriaToggle(criteria.id, e.target.checked)}
                                disabled={!isActivePhase}
                                className="flex-shrink-0"
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  cursor: isActivePhase ? 'pointer' : 'not-allowed',
                                  accentColor: '#10b981',
                                  marginTop: '2px'
                                }}
                              />
                              <span className={`text-sm ${criteria.is_completed ? 'text-gray-500 line-through' : 'text-gray-800 font-medium'}`} style={{ lineHeight: '1.5' }}>
                                {criteria.criteria_text}
                              </span>
                            </label>
                          ))}
                        </div>

                        {/* Advance Phase Button */}
                        {isActivePhase && allCriteriaCompleted && phaseIndex < projectPhases.length - 1 && (
                          <div className="mt-5 pt-5 border-t">
                            <button
                              onClick={handleAdvancePhase}
                              style={{
                                padding: '12px 24px',
                                fontSize: '15px',
                                fontWeight: '600',
                                color: 'white',
                                backgroundColor: '#10b981',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                width: '100%'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#059669';
                                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#10b981';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                              }}
                            >
                              ✓ Advance to Next Phase
                            </button>
                          </div>
                        )}

                        {isActivePhase && allCriteriaCompleted && phaseIndex === projectPhases.length - 1 && (
                          <div className="mt-5 pt-5 border-t">
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                              <p className="text-green-800 font-semibold text-sm">🎉 All criteria completed! This is the final phase.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </>
      )}

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Tasks in this Project</h2>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={hideProjectCompletedTasks}
                onChange={(e) => setHideProjectCompletedTasks(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer'
                }}
              />
              Hide completed tasks
            </label>
          </div>
        </div>
        <div className="p-6">
          {projectTasks.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No tasks yet. Create your first task!</p>
          ) : (() => {
            // Filter tasks based on completed status
            const filteredTasks = hideProjectCompletedTasks
              ? projectTasks.filter(task => task.status !== 'Completed')
              : projectTasks;

            // Group tasks by phase
            const tasksByPhase = filteredTasks.reduce((acc: { [key: string]: Task[] }, task) => {
              const phase = task.phase || 'No Phase';
              if (!acc[phase]) {
                acc[phase] = [];
              }
              acc[phase].push(task);
              return acc;
            }, {});

            // Define phase order
            const phaseOrder = ['Strategy', 'Content', 'Design', 'Front-End', 'Back-End', 'Innovate', 'No Phase'];
            const sortedPhases = Object.keys(tasksByPhase).sort((a, b) => {
              const indexA = phaseOrder.indexOf(a);
              const indexB = phaseOrder.indexOf(b);
              if (indexA === -1 && indexB === -1) return a.localeCompare(b);
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            });

            if (filteredTasks.length === 0) {
              return (
                <p className="text-gray-500 text-center py-8">
                  {hideProjectCompletedTasks ? 'No incomplete tasks. Toggle to show completed tasks.' : 'No tasks yet. Create your first task!'}
                </p>
              );
            }

            return (
              <div className="space-y-6">
                {sortedPhases.map((phase) => (
                  <div key={phase}>
                    <h3 className="text-md font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                      {phase} ({tasksByPhase[phase].length})
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                      {tasksByPhase[phase].map((task) => (
                <div
                  key={task.id}
                  style={{
                    padding: '12px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => {
                    setSelectedTask(task);
                    setShowTaskDetailModal(true);
                    loadTaskComments(task.id);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <p style={{ fontSize: '13px', fontWeight: '600', color: '#111827', margin: 0, flex: 1, paddingRight: '8px', lineHeight: '1.4' }}>
                      {task.name}
                    </p>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </div>

                  {task.description && (
                    <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', lineHeight: '1.4' }}>
                      {task.description}
                    </p>
                  )}

                  <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: '#4b5563' }}>Assigned:</span> {getAssignedToName(task.assigned_to)}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(task.status)}`}>
                        {task.status}
                      </span>
                      {task.status !== 'Completed' && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await axios.put(`${API_URL}/api/tasks/${task.id}/status`, {
                                status: 'Completed',
                                updated_by: currentUser
                              });
                              showNotification('success', 'Task marked as complete!');
                              if (selectedProject) {
                                const response = await axios.get(`${API_URL}/api/projects/${selectedProject.id}/tasks`);
                                setProjectTasks(response.data);
                              }
                              loadDashboardData();
                              loadTopProjects();
                            } catch (error) {
                              console.error('Failed to mark task as complete:', error);
                              showNotification('error', 'Failed to update task');
                            }
                          }}
                          style={{
                            padding: '2px 4px',
                            color: '#10b981',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          title="Mark as complete"
                        >
                          <CheckCircleIcon style={{ width: '14px', height: '14px' }} />
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#6b7280' }}>
                      <span>{task.estimated_hours}h</span>
                      {task.due_date && (
                        <span>• Due: {formatDate(task.due_date)}</span>
                      )}
                    </div>
                  </div>
                </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );

  const renderCampaignDetail = () => (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => updateView('campaigns')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#0d6efd',
              backgroundColor: 'transparent',
              border: '1px solid #0d6efd',
              borderRadius: '6px',
              cursor: 'pointer',
              marginBottom: '8px',
              transition: 'all 0.15s ease-in-out'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0d6efd';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#0d6efd';
            }}
          >
            ← Back to Campaigns
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {selectedCampaign?.client_name || selectedClient?.name}: {selectedCampaign?.name}
          </h1>
          <p className="text-gray-600 mt-1">{selectedCampaign?.description}</p>
          <div className="flex items-center space-x-4 mt-2 text-sm">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedCampaign?.status || '')}`}>
              {selectedCampaign?.status}
            </span>
            <span className="text-gray-500">
              Budget: {formatCurrency(selectedCampaign?.budget || 0)}
            </span>
            <span className="text-gray-500">
              Progress: {selectedCampaign?.progress_percentage}%
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => {
              setNewProjectData({...newProjectData, campaign_id: selectedCampaign?.id.toString() || ''});
              setShowNewProjectModal(true);
            }}
            className="btn-primary-green"
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#059669'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
          >
            + New Project
          </button>
          <button
            onClick={() => {
              loadAllProjects();
              setShowNewTaskModal(true);
            }}
            className="btn-primary"
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0b5ed7'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0d6efd'}
          >
            + New Task
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Projects in this Campaign</h2>
        </div>
        <div className="p-6">
          {campaignProjects.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No projects yet. Create your first project!</p>
          ) : (
            <div className="project-cards-grid">
              {campaignProjects.map((project) => (
                <div
                  key={project.id}
                  className="project-card"
                  onClick={() => handleProjectClick(project)}
                >
                  <div className="project-card-header">
                    <div className="flex-1">
                      <h3 className="project-card-title">{project.name}</h3>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingProject(project);
                        setShowEditProjectModal(true);
                      }}
                      style={{
                        padding: '6px',
                        color: '#6b7280',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                        e.currentTarget.style.color = '#111827';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                    >
                      <PencilIcon style={{ width: '16px', height: '16px' }} />
                    </button>
                  </div>

                  <p className="project-card-description">{project.description || "No description available"}</p>

                  <div className="project-card-meta">
                    <div className="project-card-meta-item">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(project.priority)} project-priority-${project.priority?.toLowerCase()}`}>
                        {project.priority}
                      </span>
                    </div>
                    <div className="project-card-meta-item">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </div>
                    {project.current_phase && (
                      <div className="project-card-meta-item">
                        <span className="text-gray-600 text-xs font-medium">
                          Phase: {project.current_phase}
                        </span>
                      </div>
                    )}
                    {project.blocked_tasks > 0 && (
                      <div className="project-card-meta-item">
                        <span className="text-red-600 font-medium text-xs">
                          {project.blocked_tasks} blocked
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="project-card-progress">
                    <div className="project-progress-label">
                      <span className="project-progress-text">Progress</span>
                      <span className="project-progress-percentage">{project.progress_percentage}%</span>
                    </div>
                    <div className="project-progress-bar">
                      <div
                        className="project-progress-fill"
                        style={{ width: `${project.progress_percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="project-card-stats">
                    <div className="project-stat">
                      <span className="project-stat-value">{project.task_count || 0}</span>
                      <div className="project-stat-label">Total Tasks</div>
                    </div>
                    <div className="project-stat">
                      <span className="project-stat-value">{project.completed_tasks || 0}</span>
                      <div className="project-stat-label">Completed</div>
                    </div>
                    <div className="project-stat">
                      <span className="project-stat-value">{project.blocked_tasks || 0}</span>
                      <div className="project-stat-label">Blocked</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderTeamTasks = () => {
    // Show ALL tasks (no week filtering)
    const allTasks = tasks.filter(task => {
      // Hide completed tasks if option is enabled
      if (hideCompletedTasks && task.status === 'Completed') return false;
      return true;
    });

    // Apply all filters
    let filteredTasks = allTasks.filter(task => {
      // Search filter
      if (teamTaskSearchQuery && !task.client_name?.toLowerCase().includes(teamTaskSearchQuery.toLowerCase())) {
        return false;
      }

      // Assignee filter
      if (selectedAssignee && task.assigned_to !== selectedAssignee) {
        return false;
      }

      // Project filter (matches "Client - Project" format)
      if (selectedProjectFilter) {
        const taskProjectDisplay = task.project_name && task.client_name ? `${task.client_name} - ${task.project_name}` : '';
        if (taskProjectDisplay !== selectedProjectFilter) {
          return false;
        }
      }

      // Phase filter
      if (selectedPhase && task.phase !== selectedPhase) {
        return false;
      }

      // Account Manager filter
      if (selectedAccountManager && task.account_manager !== selectedAccountManager) {
        return false;
      }

      return true;
    });

    // Group tasks based on grouping preference (keep existing grouping logic for display)
    const groupedTasks: { [key: string]: Task[] } = {};

    if (!selectedAssignee && !selectedAccountManager && !selectedPhase && !selectedProjectFilter) {
      // If no specific filters are selected, use the grouping logic
      if (teamTasksFilter === 'assignee') {
        filteredTasks.forEach(task => {
          const key = task.assigned_to || 'Unassigned';
          if (!groupedTasks[key]) groupedTasks[key] = [];
          groupedTasks[key].push(task);
        });
      } else if (teamTasksFilter === 'account_manager') {
        filteredTasks.forEach(task => {
          const key = task.account_manager || 'No Account Manager';
          if (!groupedTasks[key]) groupedTasks[key] = [];
          groupedTasks[key].push(task);
        });
      } else if (teamTasksFilter === 'phase') {
        filteredTasks.forEach(task => {
          const key = task.phase || 'No Phase';
          if (!groupedTasks[key]) groupedTasks[key] = [];
          groupedTasks[key].push(task);
        });
      } else {
        groupedTasks['All Tasks'] = filteredTasks;
      }
    } else {
      // When filters are active, just show all matching tasks in one group
      groupedTasks['Filtered Tasks'] = filteredTasks;
    }

    // Sort tasks within each group based on selected sort option
    const priorityOrder: { [key: string]: number } = {
      'Critical': 0,
      'High': 1,
      'Medium': 2,
      'Low': 3
    };

    // const statusOrder: { [key: string]: number } = {
    //   'Blocked': 0,
    //   'In Progress': 1,
    //   'Not Started': 2,
    //   'Client Review': 3,
    //   'Completed': 4
    // }; // Removed - unused after sorting change

    Object.keys(groupedTasks).forEach(key => {
      groupedTasks[key].sort((a, b) => {
        if (taskSortBy === 'priority') {
          // Sort by priority first, then by due date
          const priorityA = priorityOrder[a.priority] !== undefined ? priorityOrder[a.priority] : 999;
          const priorityB = priorityOrder[b.priority] !== undefined ? priorityOrder[b.priority] : 999;

          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }

          // If priority is the same, sort by due date
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;

          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        } else if (taskSortBy === 'due_date') {
          // Sort by due date first
          if (!a.due_date && !b.due_date) {
            // If neither has a due date, sort by priority
            const priorityA = priorityOrder[a.priority] !== undefined ? priorityOrder[a.priority] : 999;
            const priorityB = priorityOrder[b.priority] !== undefined ? priorityOrder[b.priority] : 999;
            return priorityA - priorityB;
          }
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;

          const dateA = new Date(a.due_date).getTime();
          const dateB = new Date(b.due_date).getTime();

          if (dateA === dateB) {
            const priorityA = priorityOrder[a.priority] !== undefined ? priorityOrder[a.priority] : 999;
            const priorityB = priorityOrder[b.priority] !== undefined ? priorityOrder[b.priority] : 999;
            return priorityA - priorityB;
          }

          return dateA - dateB;
        } else {
          // Sort by status (if needed in future)
          return 0;
        }
      });
    });

    const totalTasks = filteredTasks.length;

    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Team Tasks</h1>
            <p className="text-sm text-gray-500 mt-1">
              All active tasks sorted by due date, then priority
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-lg text-gray-900">{totalTasks}</span>
              <span className="text-gray-500 ml-1">tasks due</span>
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div className="mb-4" style={{ marginBottom: '1.5rem' }}>
          <input
            type="text"
            placeholder="Search by client name..."
            value={teamTaskSearchQuery}
            onChange={(e) => setTeamTaskSearchQuery(e.target.value)}
            className="form-control"
            style={{
              width: '200px',
              padding: '0.375rem 0.75rem',
              fontSize: '1rem',
              lineHeight: '1.5',
              color: '#212529',
              backgroundColor: '#fff',
              border: '1px solid #ced4da',
              borderRadius: '0.25rem',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#86b7fe';
              e.currentTarget.style.boxShadow = '0 0 0 0.25rem rgba(13, 110, 253, 0.25)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#ced4da';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          {teamTaskSearchQuery && (
            <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.5rem', color: '#6c757d' }}>
              Showing {filteredTasks.length} of {allTasks.length} tasks
            </p>
          )}
        </div>

        {/* Filter dropdowns */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">Filter By:</label>
            {(selectedAssignee || selectedAccountManager || selectedPhase || selectedProjectFilter) && (
              <button
                onClick={() => {
                  setSelectedAssignee('');
                  setSelectedAccountManager('');
                  setSelectedPhase('');
                  setSelectedProjectFilter('');
                  setTeamTasksFilter('all');
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear all filters
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={selectedAssignee}
              onChange={(e) => {
                setSelectedAssignee(e.target.value);
                setTeamTasksFilter('assignee');
              }}
              className="form-input"
              style={{ minWidth: '200px' }}
            >
              <option value="">Assignee</option>
              {Array.from(new Set(tasks.map(t => t.assigned_to).filter(Boolean))).sort().map(assignee => (
                <option key={assignee} value={assignee}>{assignee}</option>
              ))}
            </select>

            <select
              value={selectedProjectFilter}
              onChange={(e) => setSelectedProjectFilter(e.target.value)}
              className="form-input"
              style={{ minWidth: '250px' }}
            >
              <option value="">Project</option>
              {Array.from(new Set(tasks.map(t => t.project_name && t.client_name ? `${t.client_name} - ${t.project_name}` : '').filter(Boolean))).sort().map(projectDisplay => (
                <option key={projectDisplay} value={projectDisplay}>{projectDisplay}</option>
              ))}
            </select>

            <select
              value={selectedPhase}
              onChange={(e) => {
                setSelectedPhase(e.target.value);
                setTeamTasksFilter('phase');
              }}
              className="form-input"
              style={{ minWidth: '200px' }}
            >
              <option value="">Phase</option>
              {['Strategy', 'Content', 'Design', 'Front End', 'Back End', 'Insights & Innovation', 'Voyager'].map(phase => (
                <option key={phase} value={phase}>{phase}</option>
              ))}
            </select>

            <select
              value={selectedAccountManager}
              onChange={(e) => {
                setSelectedAccountManager(e.target.value);
                setTeamTasksFilter('account_manager');
              }}
              className="form-input"
              style={{ minWidth: '200px' }}
            >
              <option value="">Account Manager</option>
              {['Erin Lailer', 'Sally McCarthy', 'Drew McKenna', 'Mike McKenna', 'Cynde Smith'].map(am => (
                <option key={am} value={am}>{am}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Sort and filter controls */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Sort by:</label>
            <select
              value={taskSortBy}
              onChange={(e) => setTaskSortBy(e.target.value as 'status' | 'priority' | 'due_date')}
              style={{
                padding: '6px 12px',
                fontSize: '14px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              <option value="priority">Priority</option>
              <option value="status">Status</option>
              <option value="due_date">Due Date</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={hideCompletedTasks}
                onChange={(e) => setHideCompletedTasks(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer'
                }}
              />
              Hide completed tasks
            </label>
          </div>
        </div>

        {totalTasks === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <p className="text-gray-600">No tasks due this week</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupedTasks).sort().map(groupName => (
              <div key={groupName}>
                {teamTasksFilter !== 'all' && (
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">
                    {groupName} ({groupedTasks[groupName].length})
                  </h2>
                )}
                <div className="grid gap-4">
                  {groupedTasks[groupName].map((task) => (
              <div
                key={task.id}
                className="bg-white border rounded-lg p-5 shadow-sm hover:shadow-md transition-all"
                style={{
                  borderLeft: `4px solid ${
                    task.priority === 'High' ? '#dc2626' :
                    task.priority === 'Medium' ? '#f59e0b' :
                    '#10b981'
                  }`
                }}
              >
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    setSelectedTask(task);
                    setShowTaskDetailModal(true);
                    loadTaskComments(task.id);
                  }}
                >
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <span className="font-medium mr-2">Client:</span>
                    <span>{task.client_name || 'Unknown'}</span>
                  </div>
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{task.name}</h3>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(task.status)}`}>
                        {task.status}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{task.description}</p>
                  <div className="flex items-center space-x-6 text-sm text-gray-500 mb-3">
                    <div className="flex items-center">
                      <span className="font-medium mr-2">Project:</span>
                      <span>{task.project_name}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium mr-2">Phase:</span>
                      <span>{task.phase || 'Strategy'}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium mr-2">Assigned to:</span>
                      <span>{task.assigned_to}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium mr-2">Due:</span>
                      <span>{formatDate(task.due_date)}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium mr-2">Time:</span>
                      <span>{task.estimated_hours}h estimated / {task.actual_hours || 0}h actual</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-1 pt-3 border-t border-gray-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingTask(task);
                      setShowEditTaskModal(true);
                    }}
                    style={{
                      padding: '6px',
                      color: '#3b82f6',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title="Edit task"
                  >
                    <PencilIcon style={{ width: '16px', height: '16px' }} />
                  </button>
                  {task.status !== 'Completed' && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const response = await fetch(`${API_URL}/api/tasks/${task.id}/status`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              status: 'Completed',
                              updated_by: currentUser
                            })
                          });
                          if (response.ok) {
                            await loadAllTeamTasks();
                            showNotification('success', 'Task marked as complete!');
                          }
                        } catch (error) {
                          console.error('Error marking task complete:', error);
                          showNotification('error', 'Failed to mark task as complete');
                        }
                      }}
                      style={{
                        padding: '6px',
                        color: '#10b981',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Mark as complete"
                    >
                      <CheckCircleIcon style={{ width: '18px', height: '18px' }} />
                    </button>
                  )}
                </div>
              </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleTeamPriorityDragEnd = async (result: any) => {
    const { source, destination } = result;

    // Dropped outside a droppable area
    if (!destination) return;

    // No movement
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const sourceMember = source.droppableId.replace('member-', '');
    const destMember = destination.droppableId.replace('member-', '');

    // Prevent reassignment between team members
    if (sourceMember !== destMember) {
      showNotification('error', 'Cannot reassign tasks between team members from Priorities view. Use the Tasks page to reassign.');
      return;
    }

    // Get all tasks for this member
    const priorityOrder: { [key: string]: number } = {
      'Critical': 0,
      'High': 1,
      'Medium': 2,
      'Low': 3
    };

    const memberTasks = [...tasks]
      .filter(task => task.assigned_to === sourceMember && task.status !== 'Completed')
      .sort((a, b) => {
        const aPrio = a.priority || 'Medium';
        const bPrio = b.priority || 'Medium';
        const aPrioVal = priorityOrder[aPrio] !== undefined ? priorityOrder[aPrio] : 999;
        const bPrioVal = priorityOrder[bPrio] !== undefined ? priorityOrder[bPrio] : 999;
        const priorityDiff = aPrioVal - bPrioVal;
        if (priorityDiff !== 0) return priorityDiff;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      });

    // Get the task that was dragged
    const movedTask = memberTasks[source.index];
    if (!movedTask) return;

    // Instead of manual reordering, inform user that tasks auto-sort by priority
    showNotification('error', 'Tasks automatically sort by priority (Critical → High → Medium → Low). To change task order, update the task priority.');
  };

  const renderPriorities = () => {
    // Priority order for sorting
    const priorityOrder: { [key: string]: number } = {
      'Critical': 0,
      'High': 1,
      'Medium': 2,
      'Low': 3
    };

    const handleTeamPriorityMemberReorder = async (result: any) => {
      if (!result.destination) return;
      if (result.source.index === result.destination.index) return;

      const reordered = Array.from(teamMembers);
      const [removed] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, removed);

      // Update local state immediately
      setTeamMembers(reordered);

      // Save to backend
      try {
        const updates = reordered.map((member, index) => ({
          id: member.id,
          display_order: index
        }));

        await axios.put(`${API_URL}/api/team-members/reorder`, { members: updates });
        console.log('Team member order saved successfully');
      } catch (error) {
        console.error('Error saving team member order:', error);
        // Revert on error
        await loadTeamMembers();
      }
    };

    return (
      <DragDropContext onDragEnd={isReorderingTeamPriorityMembers ? handleTeamPriorityMemberReorder : handleTeamPriorityDragEnd}>
        <div className="p-6">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Team Priorities</h1>
              <p className="text-sm text-gray-500 mt-1">Top 5 tasks per team member, auto-sorted by priority (Critical → High → Medium → Low).</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTeamPrioritiesViewMode('team')}
                style={{
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: teamPrioritiesViewMode === 'team' ? '600' : '500',
                  backgroundColor: teamPrioritiesViewMode === 'team' ? '#3b82f6' : 'white',
                  color: teamPrioritiesViewMode === 'team' ? 'white' : '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: teamPrioritiesViewMode === 'team' ? '0 2px 4px rgba(59, 130, 246, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (teamPrioritiesViewMode !== 'team') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (teamPrioritiesViewMode !== 'team') {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                By Team Member
              </button>
              <button
                onClick={() => setTeamPrioritiesViewMode('phase')}
                style={{
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: teamPrioritiesViewMode === 'phase' ? '600' : '500',
                  backgroundColor: teamPrioritiesViewMode === 'phase' ? '#3b82f6' : 'white',
                  color: teamPrioritiesViewMode === 'phase' ? 'white' : '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: teamPrioritiesViewMode === 'phase' ? '0 2px 4px rgba(59, 130, 246, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (teamPrioritiesViewMode !== 'phase') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (teamPrioritiesViewMode !== 'phase') {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                By Phase
              </button>
              {teamPrioritiesViewMode === 'team' && (
                <button
                  onClick={() => setIsReorderingTeamPriorityMembers(!isReorderingTeamPriorityMembers)}
                  style={{
                    padding: '6px 14px',
                    fontSize: '13px',
                    fontWeight: '600',
                    backgroundColor: isReorderingTeamPriorityMembers ? '#10b981' : 'white',
                    color: isReorderingTeamPriorityMembers ? 'white' : '#6b7280',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    marginLeft: '8px',
                    boxShadow: isReorderingTeamPriorityMembers ? '0 2px 4px rgba(16, 185, 129, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isReorderingTeamPriorityMembers) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isReorderingTeamPriorityMembers) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  {isReorderingTeamPriorityMembers ? '✓ Done Reordering' : 'Reorder Team Members'}
                </button>
              )}
            </div>
          </div>

          {teamPrioritiesViewMode === 'team' ? (
            isReorderingTeamPriorityMembers ? (
              // Team Member Reordering Mode
              <Droppable droppableId="team-priority-members" type="TEAM_MEMBER">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '24px'
                    }}
                  >
                    {teamMembers.map((member, memberIndex) => {
                      // Filter tasks for preview
                      const allMemberTasks = tasks
                        .filter(task => task.assigned_to === member.name && task.status !== 'Completed')
                        .sort((a, b) => {
                          const aPriority = a.priority || 'Medium';
                          const bPriority = b.priority || 'Medium';
                          const aPrioVal = priorityOrder[aPriority] !== undefined ? priorityOrder[aPriority] : 999;
                          const bPrioVal = priorityOrder[bPriority] !== undefined ? priorityOrder[bPriority] : 999;
                          const priorityDiff = aPrioVal - bPrioVal;
                          if (priorityDiff !== 0) return priorityDiff;
                          if (!a.due_date) return 1;
                          if (!b.due_date) return -1;
                          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
                        });
                      const totalActiveTasks = allMemberTasks.length;

                      return (
                        <Draggable key={member.id} draggableId={`team-member-${member.id}`} index={memberIndex}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging ? '#e0e7ff' : 'white',
                                borderRadius: '8px',
                                boxShadow: snapshot.isDragging ? '0 8px 16px rgba(0, 0, 0, 0.2)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
                                border: '2px solid #e5e7eb',
                                padding: '20px'
                              }}
                            >
                              <div className="mb-4 pb-3 border-b-2 border-gray-200">
                                <div className="flex items-center gap-2" {...provided.dragHandleProps}>
                                  <span className="text-gray-400 cursor-move">⋮⋮</span>
                                  <div>
                                    <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {totalActiveTasks} active task{totalActiveTasks !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div style={{ opacity: 0.7, pointerEvents: 'none' }}>
                                {allMemberTasks.slice(0, 3).map((task) => (
                                  <div key={task.id} style={{ padding: '8px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', marginBottom: '8px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{task.name}</div>
                                  </div>
                                ))}
                                {totalActiveTasks > 3 && (
                                  <p className="text-xs text-gray-500 italic text-center mt-2">+{totalActiveTasks - 3} more</p>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ) : (
              // Normal Team Member View
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '24px'
              }}>
                {teamMembers.map((member) => {
            // Filter tasks for this team member, sort by priority, and take first 5
            const allMemberTasks = tasks
              .filter(task => task.assigned_to === member.name && task.status !== 'Completed')
              .sort((a, b) => {
                const aPriority = a.priority || 'Medium';
                const bPriority = b.priority || 'Medium';
                const aPriorityValue = priorityOrder[aPriority] !== undefined ? priorityOrder[aPriority] : 999;
                const bPriorityValue = priorityOrder[bPriority] !== undefined ? priorityOrder[bPriority] : 999;
                const priorityDiff = aPriorityValue - bPriorityValue;
                if (priorityDiff !== 0) return priorityDiff;
                // Secondary sort by due date
                if (!a.due_date) return 1;
                if (!b.due_date) return -1;
                return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
              });

            const memberTasks = allMemberTasks.slice(0, 5);
            const totalActiveTasks = allMemberTasks.length;

            return (
              <div
                key={member.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  border: '2px solid #e5e7eb',
                  padding: '20px'
                }}
              >
                <div className="mb-4 pb-3 border-b-2 border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900">
                    {member.name}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {totalActiveTasks} active task{totalActiveTasks !== 1 ? 's' : ''}
                  </p>
                </div>
                <Droppable droppableId={`member-${member.id}`}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{ display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '50px' }}
                    >
                      {memberTasks.length === 0 ? (
                        <p className="text-sm text-gray-500 italic text-center py-4">No active tasks</p>
                      ) : (
                        memberTasks.map((task, index) => (
                          <Draggable key={task.id} draggableId={`task-${task.id}`} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  padding: '10px',
                                  backgroundColor: snapshot.isDragging ? '#e0e7ff' : '#f9fafb',
                                  borderRadius: '6px',
                                  border: '1px solid #e5e7eb',
                                  cursor: 'grab',
                                  transition: 'all 0.2s'
                                }}
                                onClick={() => {
                                  if (!snapshot.isDragging) {
                                    setSelectedTask(task);
                                    setShowTaskDetailModal(true);
                                  }
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '4px' }}>
                                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', flex: 1 }}>
                                    {task.name}
                                  </div>
                                </div>
                                <p className="text-xs text-gray-600" style={{ marginBottom: '4px' }}>
                                  {task.client_name || 'Unknown'} → {task.project_name}
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px', flexWrap: 'wrap' }}>
                                  <span className="text-xs text-gray-500">
                                    {task.due_date ? formatDate(task.due_date) : 'No due date'}
                                  </span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                    {task.priority && (
                                      <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(task.priority)}`}>
                                        {task.priority}
                                      </span>
                                    )}
                                    {task.phase && (
                                      <span className={`px-2 py-0.5 rounded text-xs ${getPhaseColor(task.phase)}`}>
                                        {task.phase.includes(' - ') ? task.phase.split(' - ')[1] : task.phase}
                                      </span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(task.status)}`}>
                                      {task.status}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
                {totalActiveTasks > 5 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setSelectedAssignee(member.name);
                        updateView('team-tasks');
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline w-full text-center"
                    >
                      View all {totalActiveTasks} tasks →
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
              )
            ) : (
              // By Phase View for Team Priorities
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '24px'
              }}>
                {[
                  { name: 'Strategy - Planning', shortName: 'Planning' },
                  { name: 'Strategy - Content', shortName: 'Content' },
                  { name: 'Fulfillment - Design', shortName: 'Design' },
                  { name: 'Fulfillment - Front End', shortName: 'Front End' },
                  { name: 'Fulfillment - Back End', shortName: 'Back End' },
                  { name: 'Fulfillment - Production', shortName: 'Production' },
                  { name: 'Fulfillment - Deploy', shortName: 'Deploy' },
                  { name: 'Insights & Innovation', shortName: 'Insights' }
                ].map((phase) => {
                  // Filter tasks for this phase
                  const phaseTasks = tasks
                    .filter(task => task.phase === phase.name && task.status !== 'Completed')
                    .sort((a, b) => {
                      const aPrio = a.priority || 'Medium';
                      const bPrio = b.priority || 'Medium';
                      const aPrioVal = priorityOrder[aPrio] !== undefined ? priorityOrder[aPrio] : 999;
                      const bPrioVal = priorityOrder[bPrio] !== undefined ? priorityOrder[bPrio] : 999;
                      const priorityDiff = aPrioVal - bPrioVal;
                      if (priorityDiff !== 0) return priorityDiff;
                      if (!a.due_date) return 1;
                      if (!b.due_date) return -1;
                      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
                    })
                    .slice(0, 5);

                  const totalPhaseTasks = tasks.filter(task => task.phase === phase.name && task.status !== 'Completed').length;

                  return (
                    <div
                      key={phase.name}
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        border: '2px solid #e5e7eb',
                        padding: '20px'
                      }}
                    >
                      <div className="mb-4 pb-3 border-b-2 border-gray-200">
                        <h3 className="text-lg font-bold text-gray-900">{phase.shortName}</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {totalPhaseTasks} active task{totalPhaseTasks !== 1 ? 's' : ''}
                        </p>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '50px' }}>
                        {phaseTasks.length === 0 ? (
                          <p className="text-sm text-gray-500 italic text-center py-4">No active tasks</p>
                        ) : (
                          phaseTasks.map((task) => (
                            <div
                              key={task.id}
                              style={{
                                padding: '10px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                setSelectedTask(task);
                                setShowTaskDetailModal(true);
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '4px' }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', flex: 1 }}>
                                  {task.name}
                                </div>
                              </div>
                              <p className="text-xs text-gray-600" style={{ marginBottom: '4px' }}>
                                {task.assigned_to || 'Unassigned'} • {task.client_name || 'Unknown'} → {task.project_name}
                              </p>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px', flexWrap: 'wrap' }}>
                                <span className="text-xs text-gray-500">
                                  {task.due_date ? formatDate(task.due_date) : 'No due date'}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                  {task.priority && (
                                    <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(task.priority)}`}>
                                      {task.priority}
                                    </span>
                                  )}
                                  <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(task.status)}`}>
                                    {task.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {totalPhaseTasks > 5 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-sm text-gray-600 text-center">
                            +{totalPhaseTasks - 5} more task{totalPhaseTasks - 5 !== 1 ? 's' : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
      </div>
    </DragDropContext>
    );
  };

  // Removed - replaced by handleAccountPriorityDragEnd
  // const handlePriorityDragEnd = (result: any) => {
  //   const { source, destination } = result;

  //   // Dropped outside the list
  //   if (!destination) return;

  //   // No movement
  //   if (source.droppableId === destination.droppableId && source.index === destination.index) {
  //     return;
  //   }

  //   // Extract manager IDs from droppableId (format: "manager-123")
  //   const sourceManagerId = parseInt(source.droppableId.split('-')[1]);
  //   const destManagerId = parseInt(destination.droppableId.split('-')[1]);

  //   // Check if moving between different managers
  //   if (sourceManagerId !== destManagerId) {
  //     // Moving to a different manager
  //     const sourcePriorities = [...(accountManagerPriorities[sourceManagerId] || [])];
  //     const destPriorities = [...(accountManagerPriorities[destManagerId] || [])];

  //     // Remove from source
  //     const [removed] = sourcePriorities.splice(source.index, 1);

  //     // Update the priority's account_manager_id
  //     removed.account_manager_id = destManagerId;

  //     // Add to destination
  //     destPriorities.splice(destination.index, 0, removed);

  //     // Update local state
  //     const newPriorities = {
  //       ...accountManagerPriorities,
  //       [sourceManagerId]: sourcePriorities,
  //       [destManagerId]: destPriorities
  //     };
  //     setAccountManagerPriorities(newPriorities);

  //     // Persist the move to backend
  //     movePriorityToManager(removed.id, destManagerId, destPriorities);
  //   } else {
  //     // Reordering within the same manager
  //     const priorities = [...(accountManagerPriorities[sourceManagerId] || [])];

  //     // Reorder the array
  //     const [removed] = priorities.splice(source.index, 1);
  //     priorities.splice(destination.index, 0, removed);

  //     // Update local state with new object reference
  //     const newPriorities = {
  //       ...accountManagerPriorities,
  //       [sourceManagerId]: priorities
  //     };
  //     setAccountManagerPriorities(newPriorities);

  //     // Persist the new order to backend
  //     savePriorityOrder(sourceManagerId, priorities);
  //   }
  // };

  // Removed - no longer needed after removing handlePriorityDragEnd
  // const savePriorityOrder = async (managerId: number, priorities: AccountManagerPriority[]) => {
  //   try {
  //     // Send the new order to backend
  //     const priorityIds = priorities.map(p => p.id);
  //     await axios.put(`${API_URL}/api/account-managers/${managerId}/priorities/reorder`, {
  //       priority_ids: priorityIds
  //     });
  //     console.log('Priority order saved successfully');
  //   } catch (error: any) {
  //     console.error('Failed to save priority order:', error);
  //     console.error('Error details:', error.response?.data || error.message);
  //     showNotification('error', `Failed to save order: ${error.response?.data?.error || error.message}`);
  //   }
  // };

  // Removed - no longer needed after removing handlePriorityDragEnd
  // const movePriorityToManager = async (priorityId: number, newManagerId: number, destPriorities: AccountManagerPriority[]) => {
  //   try {
  //     // First, move the priority to the new manager
  //     await axios.put(`${API_URL}/api/account-manager-priorities/${priorityId}/move`, {
  //       new_account_manager_id: newManagerId
  //     });

  //     // Then, update the order in the destination list
  //     await savePriorityOrder(newManagerId, destPriorities);

  //     console.log('Priority moved successfully');
  //   } catch (error: any) {
  //     console.error('Failed to move priority:', error);
  //     console.error('Error details:', error.response?.data || error.message);
  //     showNotification('error', `Failed to move priority: ${error.response?.data?.error || error.message}`);
  //     // Reload to revert the optimistic update
  //     loadAccountManagers();
  //   }
  // };

  const renderVoyager = () => {
    // Filter projects to only show Voyager projects
    const voyagerProjects = projects.filter(p => p.project_type === 'Voyager');

    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Voyager Projects</h1>
          <p className="text-sm text-gray-500 mt-1">Monthly SEO reporting and optimization projects</p>
        </div>

        {voyagerProjects.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
            <SpyglassIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No Voyager projects yet</p>
            <button
              onClick={async () => {
                updateView('project-management');
                setProjectCreationMode('advanced');
                await loadAllCampaigns();
                setShowNewProjectModal(true);
                setSelectedProjectType('Voyager');
              }}
              className="btn-primary"
            >
              Create Voyager Project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {voyagerProjects.map((project) => {
              const campaign = campaigns.find(c => c.id === project.campaign_id);
              const client = dashboardData.clients.find((cl: Client) => cl.id === campaign?.client_id);

              return (
                <div
                  key={project.id}
                  className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedProject(project);
                    updateView('project-details');
                  }}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {project.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {client?.name || project.client_name || 'Unknown Client'}
                        </p>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {project.voyager_level || 'No Level'}
                        </span>
                      </div>
                    </div>

                    {project.client_url && (
                      <div className="mb-3 text-sm text-gray-600">
                        <a
                          href={project.client_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {project.client_url}
                        </a>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>{campaign?.name || project.campaign_name || 'No Campaign'}</span>
                      <span className="text-xs text-gray-500">
                        {project.created_at ? `Created ${new Date(project.created_at).toLocaleDateString()}` : ''}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderUsers = () => {
    if (teamMembers.length === 0) {
      return (
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    return (
      <div className="p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Users</h1>
            <p className="text-sm text-gray-500 mt-1">Manage team members and their access</p>
          </div>
          <button
            onClick={handleCreateUser}
            className="btn-primary"
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            + Create User
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Slack ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teamMembers
                .sort((a: any, b: any) => a.name.localeCompare(b.name))
                .map((member: any) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{member.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{member.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {member.role_name || 'No Role'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {member.slack_user_id ? (
                          <code className="px-2 py-1 bg-gray-100 rounded text-xs">{member.slack_user_id}</code>
                        ) : (
                          <span className="text-gray-400 italic">Not set</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        member.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {member.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditUser(member)}
                        style={{
                          padding: '6px 16px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: 'white',
                          backgroundColor: '#3b82f6',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          marginRight: '8px',
                          transition: 'all 0.2s',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#2563eb';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#3b82f6';
                          e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteUser(member)}
                        style={{
                          padding: '6px 16px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: 'white',
                          backgroundColor: '#ef4444',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#dc2626';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#ef4444';
                          e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleAccountPriorityDragEnd = async (result: any) => {
    const { source, destination } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Get account manager ID from droppableId (format: "manager-{id}")
    const managerId = parseInt(source.droppableId.replace('manager-', ''));

    // Priority sort order: Critical > High > Medium > Low
    const priorityOrder: { [key: string]: number } = {
      'Critical': 0,
      'High': 1,
      'Medium': 2,
      'Low': 3
    };

    // Get projects for this manager, sorted the SAME way they are displayed
    const managerProjects = projects
      .filter(p => p.account_manager_id === managerId)
      .sort((a, b) => {
        // Primary sort: by priority
        const aPriority = priorityOrder[a.priority] ?? 999;
        const bPriority = priorityOrder[b.priority] ?? 999;
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        // Secondary sort: by display_order
        return (a.display_order || 0) - (b.display_order || 0);
      });

    // Reorder the projects
    const reorderedProjects = Array.from(managerProjects);
    const [removed] = reorderedProjects.splice(source.index, 1);
    reorderedProjects.splice(destination.index, 0, removed);

    // Update local state immediately for smooth UX
    const updatedProjects = projects.map(p => {
      if (p.account_manager_id !== managerId) return p;
      const index = reorderedProjects.findIndex(rp => rp.id === p.id);
      return { ...p, display_order: index };
    });
    setProjects(updatedProjects);

    // Save new order to backend
    try {
      const projectsToUpdate = reorderedProjects.map((p, index) => ({
        id: p.id,
        display_order: index
      }));

      await axios.put(`${API_URL}/api/projects/reorder`, {
        projects: projectsToUpdate
      });

      console.log('Project order saved successfully');
    } catch (error) {
      console.error('Error saving project order:', error);
      // Revert to original order on error
      await loadAllProjects();
    }
  };

  const handleTeamMemberReorder = async (result: any) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const reordered = Array.from(accountManagers);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    // Update local state immediately
    setAccountManagers(reordered);

    // Save to backend - update display_order for each manager
    try {
      const updates = reordered.map((manager, index) => ({
        id: manager.id,
        display_order: index
      }));

      await axios.put(`${API_URL}/api/account-managers/reorder`, { managers: updates });
      console.log('Team member order saved successfully');
    } catch (error) {
      console.error('Error saving team member order:', error);
      // Revert on error
      await loadAccountManagers();
    }
  };

  const renderAccountDetails = () => {
    // Load account managers dropdown list if not loaded
    if (accountManagers.length === 0) {
      loadAccountManagers();
    }

    // Load clients when account manager is selected
    if (selectedAccountManagerId && accountManagerClients.length === 0) {
      loadAccountManagerClients(selectedAccountManagerId);
    }

    const selectedManager = accountManagers.find(m => m.id === selectedAccountManagerId);

    const handleQuickSave = async (clientId: number, field: string, value: string) => {
      try {
        const clientToUpdate = accountManagerClients.find(c => c.id === clientId);
        const updateData = {
          ...clientToUpdate,
          [field]: value
        };

        const response = await axios.put(`${API_URL}/api/clients/${clientId}`, updateData);
        if (response.data.success) {
          // Update local state
          setAccountManagerClients(accountManagerClients.map(c =>
            c.id === clientId ? { ...c, [field]: value } : c
          ));
          showNotification('success', 'Contact information updated');
        }
      } catch (error) {
        console.error('Failed to update contact:', error);
        showNotification('error', 'Failed to update contact information');
      }
    };

    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #dee2e6',
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '600',
            color: '#212529',
            margin: 0
          }}>Account Details</h1>

          <select
            value={selectedAccountManagerId || ''}
            onChange={(e) => {
              const id = parseInt(e.target.value);
              setSelectedAccountManagerId(id);
              setAccountManagerClients([]); // Clear old clients
              if (id) {
                loadAccountManagerClients(id);
              }
            }}
            style={{
              width: '280px',
              padding: '10px 14px',
              fontSize: '15px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              backgroundColor: 'white',
              color: '#212529',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="">Select Account Manager</option>
            {accountManagers.map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.name}
              </option>
            ))}
          </select>
        </div>

        {!selectedAccountManagerId ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa'
          }}>
            <p style={{ fontSize: '16px', color: '#6c757d' }}>Please select an account manager to view their clients</p>
          </div>
        ) : accountManagerClients.length === 0 ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa'
          }}>
            <p style={{ fontSize: '16px', color: '#6c757d' }}>No clients found for {selectedManager?.name}</p>
          </div>
        ) : (
          <div style={{ flex: 1, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 10 }}>
                <tr>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Client Name
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Contact Name
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Email
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Phone
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Campaigns
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Projects
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Tasks
                  </th>
                </tr>
              </thead>
              <tbody>
                {accountManagerClients.map((client, index) => (
                  <tr
                    key={client.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f8f9fa'}
                  >
                    <td style={{ padding: '20px 32px', borderBottom: '1px solid #dee2e6' }}>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: '#212529' }}>{client.name}</div>
                    </td>
                    <td style={{ padding: '20px 32px', borderBottom: '1px solid #dee2e6' }}>
                      <input
                        type="text"
                        value={client.company || ''}
                        onChange={(e) => {
                          setAccountManagerClients(accountManagerClients.map(c =>
                            c.id === client.id ? { ...c, company: e.target.value } : c
                          ));
                        }}
                        onBlur={(e) => handleQuickSave(client.id, 'company', e.target.value)}
                        style={{
                          fontSize: '15px',
                          color: '#212529',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: '1px solid transparent',
                          padding: '4px 0',
                          width: '100%',
                          outline: 'none',
                          transition: 'border-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.borderBottomColor = '#ced4da'}
                        onMouseLeave={(e) => {
                          if (document.activeElement !== e.currentTarget) {
                            e.currentTarget.style.borderBottomColor = 'transparent';
                          }
                        }}
                        onFocus={(e) => e.currentTarget.style.borderBottomColor = '#0d6efd'}
                        placeholder="Enter contact name"
                      />
                    </td>
                    <td style={{ padding: '20px 32px', borderBottom: '1px solid #dee2e6' }}>
                      <input
                        type="email"
                        value={client.contact_email || ''}
                        onChange={(e) => {
                          setAccountManagerClients(accountManagerClients.map(c =>
                            c.id === client.id ? { ...c, contact_email: e.target.value } : c
                          ));
                        }}
                        onBlur={(e) => handleQuickSave(client.id, 'contact_email', e.target.value)}
                        style={{
                          fontSize: '15px',
                          color: '#212529',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: '1px solid transparent',
                          padding: '4px 0',
                          width: '100%',
                          outline: 'none',
                          transition: 'border-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.borderBottomColor = '#ced4da'}
                        onMouseLeave={(e) => {
                          if (document.activeElement !== e.currentTarget) {
                            e.currentTarget.style.borderBottomColor = 'transparent';
                          }
                        }}
                        onFocus={(e) => e.currentTarget.style.borderBottomColor = '#0d6efd'}
                        placeholder="Enter email"
                      />
                    </td>
                    <td style={{ padding: '20px 32px', borderBottom: '1px solid #dee2e6' }}>
                      <input
                        type="tel"
                        value={client.contact_phone || ''}
                        onChange={(e) => {
                          setAccountManagerClients(accountManagerClients.map(c =>
                            c.id === client.id ? { ...c, contact_phone: e.target.value } : c
                          ));
                        }}
                        onBlur={(e) => handleQuickSave(client.id, 'contact_phone', e.target.value)}
                        style={{
                          fontSize: '15px',
                          color: '#212529',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: '1px solid transparent',
                          padding: '4px 0',
                          width: '100%',
                          outline: 'none',
                          transition: 'border-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.borderBottomColor = '#ced4da'}
                        onMouseLeave={(e) => {
                          if (document.activeElement !== e.currentTarget) {
                            e.currentTarget.style.borderBottomColor = 'transparent';
                          }
                        }}
                        onFocus={(e) => e.currentTarget.style.borderBottomColor = '#0d6efd'}
                        placeholder="Enter phone"
                      />
                    </td>
                    <td style={{ padding: '20px 32px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#0d6efd' }}>{client.campaign_count}</span>
                    </td>
                    <td style={{ padding: '20px 32px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#6f42c1' }}>{client.project_count}</span>
                    </td>
                    <td style={{ padding: '20px 32px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                      <span style={{ fontSize: '18px', fontWeight: '700', color: '#fd7e14' }}>{client.task_count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderAccountOverview = () => {
    // Load stats when this view is rendered
    if (accountManagerStats.length === 0) {
      loadAccountManagerStats();
    }

    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #dee2e6',
          backgroundColor: 'white'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '600',
            color: '#212529',
            margin: 0
          }}>Account Manager Overview</h1>
        </div>

        {accountManagerStats.length === 0 ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa'
          }}>
            <p style={{ fontSize: '16px', color: '#6c757d' }}>No account managers with active work found.</p>
          </div>
        ) : (
          <div style={{ flex: 1, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 10 }}>
                <tr>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Account Manager
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Clients
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Campaigns
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Projects
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Tasks
                  </th>
                  <th style={{
                    padding: '16px 32px',
                    textAlign: 'right',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {accountManagerStats.map((manager, index) => (
                  <tr
                    key={manager.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f8f9fa'}
                    onClick={() => {
                      setSelectedAccountManagerId(manager.id);
                      updateView('account-details');
                    }}
                  >
                    <td style={{ padding: '20px 32px', borderBottom: '1px solid #dee2e6' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#212529' }}>{manager.name}</div>
                      {manager.email && (
                        <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '4px' }}>{manager.email}</div>
                      )}
                    </td>
                    <td style={{ padding: '20px 32px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                      <span style={{ fontSize: '24px', fontWeight: '700', color: '#0d6efd' }}>{manager.client_count}</span>
                    </td>
                    <td style={{ padding: '20px 32px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                      <span style={{ fontSize: '24px', fontWeight: '700', color: '#198754' }}>{manager.open_campaign_count}</span>
                    </td>
                    <td style={{ padding: '20px 32px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                      <span style={{ fontSize: '24px', fontWeight: '700', color: '#6f42c1' }}>{manager.open_project_count}</span>
                    </td>
                    <td style={{ padding: '20px 32px', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>
                      <span style={{ fontSize: '24px', fontWeight: '700', color: '#fd7e14' }}>{manager.open_task_count}</span>
                    </td>
                    <td style={{ padding: '20px 32px', textAlign: 'right', borderBottom: '1px solid #dee2e6' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAccountManagerId(manager.id);
                          updateView('account-details');
                        }}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#0d6efd',
                          backgroundColor: 'transparent',
                          border: '1px solid #0d6efd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#0d6efd';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#0d6efd';
                        }}
                      >
                        View Details →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderAccountManagers = () => {
    // Show loading spinner while data is being fetched
    if (accountManagers.length === 0 || projects.length === 0) {
      return (
        <div className="p-6 flex flex-col items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading account priorities...</p>
        </div>
      );
    }

    return (
      <DragDropContext onDragEnd={isReorderingTeamMembers ? handleTeamMemberReorder : handleAccountPriorityDragEnd}>
        <div className="p-6">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Account Priorities</h1>
              <p className="text-sm text-gray-500 mt-1">Active projects for each account manager</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPrioritiesViewMode('team')}
                style={{
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: prioritiesViewMode === 'team' ? '600' : '500',
                  backgroundColor: prioritiesViewMode === 'team' ? '#3b82f6' : 'white',
                  color: prioritiesViewMode === 'team' ? 'white' : '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: prioritiesViewMode === 'team' ? '0 2px 4px rgba(59, 130, 246, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (prioritiesViewMode !== 'team') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (prioritiesViewMode !== 'team') {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                By Team Member
              </button>
              <button
                onClick={() => setPrioritiesViewMode('phase')}
                style={{
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: prioritiesViewMode === 'phase' ? '600' : '500',
                  backgroundColor: prioritiesViewMode === 'phase' ? '#3b82f6' : 'white',
                  color: prioritiesViewMode === 'phase' ? 'white' : '#6b7280',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: prioritiesViewMode === 'phase' ? '0 2px 4px rgba(59, 130, 246, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (prioritiesViewMode !== 'phase') {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (prioritiesViewMode !== 'phase') {
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                By Phase
              </button>
              {prioritiesViewMode === 'team' && (
                <button
                  onClick={() => setIsReorderingTeamMembers(!isReorderingTeamMembers)}
                  style={{
                    padding: '6px 14px',
                    fontSize: '13px',
                    fontWeight: '600',
                    backgroundColor: isReorderingTeamMembers ? '#10b981' : 'white',
                    color: isReorderingTeamMembers ? 'white' : '#6b7280',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    marginLeft: '8px',
                    boxShadow: isReorderingTeamMembers ? '0 2px 4px rgba(16, 185, 129, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isReorderingTeamMembers) {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isReorderingTeamMembers) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  {isReorderingTeamMembers ? '✓ Done Reordering' : 'Reorder Team Members'}
                </button>
              )}
            </div>
          </div>

          {prioritiesViewMode === 'team' ? (
            isReorderingTeamMembers ? (
              // Team Member Reordering Mode - entire columns are draggable
              <Droppable droppableId="team-members" type="TEAM_MEMBER">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '24px'
                    }}
                  >
                    {accountManagers.map((manager, index) => {
                      // Priority sort order: Critical > High > Medium > Low
                      const priorityOrder: { [key: string]: number } = {
                        'Critical': 0,
                        'High': 1,
                        'Medium': 2,
                        'Low': 3
                      };

                      // Filter and sort projects for this account manager by priority, then display_order
                      const managerProjects = projects
                        .filter(p => p.account_manager_id === manager.id && p.status !== 'Completed')
                        .sort((a, b) => {
                          // Primary sort: by priority
                          const aPriority = priorityOrder[a.priority] ?? 999;
                          const bPriority = priorityOrder[b.priority] ?? 999;
                          if (aPriority !== bPriority) {
                            return aPriority - bPriority;
                          }
                          // Secondary sort: by display_order
                          return (a.display_order || 0) - (b.display_order || 0);
                        });

                      return (
                        <Draggable key={manager.id} draggableId={`team-${manager.id}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging ? '#e0e7ff' : 'white',
                                borderRadius: '8px',
                                boxShadow: snapshot.isDragging ? '0 8px 16px rgba(0, 0, 0, 0.2)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
                                border: '2px solid #e5e7eb',
                                padding: '20px'
                              }}
                            >
                              <div className="mb-4 pb-3 border-b-2 border-gray-200">
                                <div className="flex justify-between items-start">
                                  <div className="flex items-center gap-2" {...provided.dragHandleProps}>
                                    <span className="text-gray-400 cursor-move">⋮⋮</span>
                                    <div>
                                      <h3 className="text-lg font-bold text-gray-900">
                                        {manager.name}
                                      </h3>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {managerProjects.length} project{managerProjects.length !== 1 ? 's' : ''}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: '50px', opacity: 0.7 }}>
                                {managerProjects.length === 0 ? (
                                  <p className="text-sm text-gray-500 italic text-center py-4">No active projects</p>
                                ) : (
                                  managerProjects.slice(0, 5).map((project) => (
                                    <div
                                      key={project.id}
                                      style={{
                                        padding: '10px',
                                        backgroundColor: '#f9fafb',
                                        borderRadius: '6px',
                                        border: '1px solid #e5e7eb',
                                        pointerEvents: 'none'
                                      }}
                                    >
                                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                                        {project.client_name}: {project.name}
                                      </div>
                                    </div>
                                  ))
                                )}
                                {managerProjects.length > 5 && (
                                  <p className="text-xs text-gray-500 italic text-center">
                                    +{managerProjects.length - 5} more
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ) : (
              // Normal Team Member View - projects are draggable
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '24px'
              }}>
                {accountManagers.map((manager) => {
              // const priorities = accountManagerPriorities[manager.id] || []; // Removed - unused

              // Priority sort order: Critical > High > Medium > Low
              const priorityOrder: { [key: string]: number } = {
                'Critical': 0,
                'High': 1,
                'Medium': 2,
                'Low': 3
              };

              // Filter and sort projects for this account manager by priority, then display_order
              const managerProjects = projects
                .filter(p => p.account_manager_id === manager.id && p.status !== 'Completed')
                .sort((a, b) => {
                  // Primary sort: by priority
                  const aPriority = priorityOrder[a.priority] ?? 999;
                  const bPriority = priorityOrder[b.priority] ?? 999;
                  if (aPriority !== bPriority) {
                    return aPriority - bPriority;
                  }
                  // Secondary sort: by display_order
                  return (a.display_order || 0) - (b.display_order || 0);
                });

              return (
                <div
                  key={manager.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    border: '2px solid #e5e7eb',
                    padding: '20px'
                  }}
                >
                  <div className="mb-4 pb-3 border-b-2 border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {manager.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {managerProjects.length} project{managerProjects.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <button
                        className="btn-primary"
                        onClick={async () => {
                          setSelectedAccountManagerForPriority(manager.id);
                          await loadAllCampaigns(); // Load all campaigns before opening modal
                          await loadDashboardData(); // Load all clients
                          setShowClientCampaignSelectModal(true);
                          setSelectedClientForProject(null);
                          setSelectedCampaignForProject(null);
                          setShowNewClientInline(false);
                          setShowNewCampaignInline(false);
                        }}
                        style={{
                          padding: '4px 10px',
                          fontSize: '12px',
                          fontWeight: '600',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        + New
                      </button>
                    </div>
                  </div>

                  <Droppable droppableId={`manager-${manager.id}`}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: '50px' }}
                      >
                        {managerProjects.length === 0 ? (
                          <p className="text-sm text-gray-500 italic text-center py-4">No active projects</p>
                        ) : (
                          managerProjects.map((project, index) => {
                            // Check if project has no account_manager_id assigned (highlighting needed)
                            const hasNoAccountManager = !project.account_manager_id;

                            return (
                            <Draggable key={project.id} draggableId={`project-${project.id}`} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    ...provided.draggableProps.style,
                                    padding: '10px',
                                    backgroundColor: snapshot.isDragging ? '#e0e7ff' : (hasNoAccountManager ? '#fff7ed' : '#f9fafb'),
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    cursor: 'grab',
                                    transition: 'all 0.2s'
                                  }}
                                  onClick={() => {
                                    if (!snapshot.isDragging) {
                                      // Check if project is under "Ongoing Projects" (client_id: 999)
                                      if (project.client_id === 999) {
                                        // Open Move dialog for Ongoing Projects
                                        setProjectToMove(project);
                                        setMoveProjectData({ client_id: '', campaign_id: project.campaign_id?.toString() || '' });
                                        setShowMoveProjectModal(true);
                                      } else {
                                        // Open Edit dialog for regular projects
                                        setQuickEditProject(project);
                                        setShowQuickEditModal(true);
                                      }
                                    }
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '4px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', flex: 1 }}>
                                      {project.client_name}: {project.name}
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Check if project is under "Ongoing Projects" (client_id: 999)
                                        if (project.client_id === 999) {
                                          // Open Move dialog for Ongoing Projects
                                          setProjectToMove(project);
                                          setMoveProjectData({ client_id: '', campaign_id: project.campaign_id?.toString() || '' });
                                          setShowMoveProjectModal(true);
                                        } else {
                                          // Open Edit dialog for regular projects
                                          setQuickEditProject(project);
                                          setShowQuickEditModal(true);
                                        }
                                      }}
                                      style={{
                                        padding: '2px 4px',
                                        color: '#6b7280',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#e5e7eb';
                                        e.currentTarget.style.color = '#374151';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = '#6b7280';
                                      }}
                                      title={project.client_id === 999 ? "Move project" : "Edit project"}
                                    >
                                      <PencilIcon style={{ width: '12px', height: '12px' }} />
                                    </button>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', flexWrap: 'wrap' }}>
                                    {project.priority && (
                                      <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(project.priority)}`}>
                                        {project.priority}
                                      </span>
                                    )}
                                    {project.phase && (
                                      <span className={`px-2 py-0.5 rounded text-xs ${getPhaseColor(project.phase)}`}>
                                        {project.phase.includes(' - ') ? project.phase.split(' - ')[1] : project.phase}
                                      </span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(project.status)}`}>
                                      {project.status}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                            );
                          })
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
            )
          ) : (
            // By Phase View
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '24px'
            }}>
              {[
                { name: 'Strategy - Planning', shortName: 'Planning', color: '#e0e7ff' },
                { name: 'Strategy - Content', shortName: 'Content', color: '#ddd6fe' },
                { name: 'Fulfillment - Design', shortName: 'Design', color: '#fce7f3' },
                { name: 'Fulfillment - Front End', shortName: 'Front End', color: '#fce7f3' },
                { name: 'Fulfillment - Back End', shortName: 'Back End', color: '#fce7f3' },
                { name: 'Fulfillment - Production', shortName: 'Production', color: '#fce7f3' },
                { name: 'Fulfillment - Deploy', shortName: 'Deploy', color: '#fce7f3' },
                { name: 'Insights & Innovation', shortName: 'Insights', color: '#c7d2fe' }
              ].map((phase) => {
                // Priority sort order: Critical > High > Medium > Low
                const priorityOrder: { [key: string]: number } = {
                  'Critical': 0,
                  'High': 1,
                  'Medium': 2,
                  'Low': 3
                };

                // Filter projects for this phase
                const phaseProjects = projects
                  .filter(p => p.phase === phase.name && p.status !== 'Completed')
                  .sort((a, b) => {
                    // Primary sort: by priority
                    const aPriority = priorityOrder[a.priority] ?? 999;
                    const bPriority = priorityOrder[b.priority] ?? 999;
                    if (aPriority !== bPriority) {
                      return aPriority - bPriority;
                    }
                    // Secondary sort: by display_order
                    return (a.display_order || 0) - (b.display_order || 0);
                  });

                return (
                  <div
                    key={phase.name}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      border: '2px solid #e5e7eb',
                      padding: '20px'
                    }}
                  >
                    <div className="mb-4 pb-3 border-b-2 border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            {phase.shortName}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1">
                            {phaseProjects.length} project{phaseProjects.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minHeight: '50px' }}>
                      {phaseProjects.length === 0 ? (
                        <p className="text-sm text-gray-500 italic text-center py-4">No active projects</p>
                      ) : (
                        phaseProjects.map((project) => {
                          const manager = accountManagers.find(m => m.id === project.account_manager_id);
                          return (
                            <div
                              key={project.id}
                              style={{
                                padding: '10px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                // Check if project is under "Ongoing Projects" (client_id: 999)
                                if (project.client_id === 999) {
                                  // Open Move dialog for Ongoing Projects
                                  setProjectToMove(project);
                                  setMoveProjectData({ client_id: '', campaign_id: project.campaign_id?.toString() || '' });
                                  setShowMoveProjectModal(true);
                                } else {
                                  // Open Edit dialog for regular projects
                                  setQuickEditProject(project);
                                  setShowQuickEditModal(true);
                                }
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '4px' }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827', flex: 1 }}>
                                  {project.client_name}: {project.name}
                                </div>
                              </div>
                              <p className="text-xs text-gray-600" style={{ marginBottom: '4px' }}>
                                {manager?.name || 'Unassigned'}
                              </p>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', flexWrap: 'wrap' }}>
                                {project.priority && (
                                  <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(project.priority)}`}>
                                    {project.priority}
                                  </span>
                                )}
                                <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(project.status)}`}>
                                  {project.status}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DragDropContext>
    );
  };

  const getCurrentContent = () => {
    switch (currentView) {
      case 'overview':
        return renderOverview();
      case 'client-detail':
        return renderClientDetail();
      case 'campaigns':
        return renderCampaignsView();
      case 'campaign-detail':
        return renderCampaignDetail();
      case 'project-detail':
        return renderProjectDetail();
      case 'project-management':
        return renderProjectManagement();
      case 'account-management':
        return renderAccountManagement();
      case 'technician':
        return renderTechnicianView();
      case 'team-tasks':
        return renderTeamTasks();
      case 'priorities':
        return renderPriorities();
      case 'users':
        return renderUsers();
      case 'activity-log':
        return <ActivityLog currentUser={currentUser} />;
      case 'account-managers':
        return renderAccountManagers();
      case 'account-overview':
        return renderAccountOverview();
      case 'account-details':
        return renderAccountDetails();
      default:
        return renderOverview();
    }
  };

  const getRoleBasedNavItems = () => {
    const allowedUsersManagers = ['Drew McKenna', 'Mike McKenna', 'Cynde Smith'];
    const hasUsersAccess = allowedUsersManagers.includes(currentUser);

    const sections = [
      {
        title: null,
        items: [
          { id: 'overview', label: 'Dashboard', icon: HomeIcon }
        ]
      },
      {
        title: 'CLIENTS',
        items: [
          { id: 'account-management', label: 'Clients', icon: UserGroupIcon },
          { id: 'campaigns', label: 'Campaigns', icon: BriefcaseIcon },
          { id: 'project-management', label: 'Projects', icon: ClipboardDocumentListIcon },
          { id: 'team-tasks', label: 'Tasks', icon: ClipboardDocumentCheckIcon }
        ]
      },
      {
        title: 'PRIORITIES',
        items: [
          { id: 'account-managers', label: 'Account Priorities', icon: UserIcon },
          { id: 'priorities', label: 'Team Priorities', icon: StarIcon }
        ]
      },
      {
        title: 'ACCOUNTS',
        items: [
          { id: 'account-overview', label: 'Managers', icon: UserIcon },
          { id: 'account-details', label: 'Details', icon: BuildingOfficeIcon }
        ]
      },
      ...(hasUsersAccess ? [{
        title: 'ADMIN',
        items: [
          { id: 'users', label: 'Users', icon: UserGroupIcon },
          { id: 'activity-log', label: 'Activity Log', icon: ClipboardDocumentListIcon }
        ]
      }] : []),
      ...(!hasUsersAccess ? [{
        title: null,
        items: [
          { id: 'activity-log', label: 'Activity Log', icon: ClipboardDocumentListIcon }
        ]
      }] : [])
    ];

    return sections;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <img src="https://propagate.team/images/propagate-logo.svg" alt="Propagate" className="logo logo-header" />
            <div className="connection-status">
              <div className={`status-dot ${connectionStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span>API {connectionStatus}</span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Role Switcher - Hidden as unnecessary */}
            {/* <div className="role-select-container">
              <label htmlFor="role-select" className="role-select-label">
                Role:
              </label>
              <select
                id="role-select"
                value={userRole}
                onChange={(e) => setUserRole(e.target.value as UserRole)}
                className="role-select"
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              >
                <option value="account-manager">Account Manager</option>
                <option value="project-manager">Project Manager</option>
                <option value="technician">Technician</option>
              </select>
            </div> */}
          </div>
        </div>
      </header>

      {/* Notification System */}
      <div className="notifications-container">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`notification ${notification.type === 'success' ? 'notification-success' : 'notification-error'}`}
          >
            <div className="notification-content">
              {notification.type === 'success' ? (
                <CheckCircleIcon className="icon-20" />
              ) : (
                <svg className="icon-20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <span className="notification-text">{notification.message}</span>
            </div>
            <button
              onClick={() => dismissNotification(notification.id)}
              className="notification-close"
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
            >
              <svg className="icon-16" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      <div className="main-layout">
        {/* Sidebar */}
        <aside className="sidebar">
          <div className="sidebar-content">
            <div className="sidebar-header">
              <h2 className="sidebar-title">Client Management</h2>
              <p className="sidebar-date">{new Date().toLocaleDateString()}</p>
            </div>

            <nav className="nav">
              {getRoleBasedNavItems().map((section, sectionIndex) => (
                <div key={sectionIndex} style={{ marginBottom: '20px' }}>
                  {section.title && (
                    <div style={{
                      padding: '8px 20px',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: '#6b7280',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase'
                    }}>
                      {section.title}
                    </div>
                  )}
                  {section.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => updateView(item.id)}
                      className={`nav-item ${currentView === item.id ? 'active' : ''}`}
                    >
                      <item.icon className="nav-icon" />
                      <span className="nav-label">{item.label}</span>
                    </button>
                  ))}
                </div>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="content">
          {getCurrentContent()}
        </main>
      </div>

      {/* New Client Modal */}
      {showNewClientModal && (
        <div className={showMoveProjectModal ? "modal-backdrop-nested" : "modal-backdrop"}>
          <div className="modal-content">
            <h2 className="modal-title">Create New Client</h2>

            <div className="form-group">
              <label className="form-label">Company Name *</label>
              <input
                type="text"
                value={newClientData.name}
                onChange={(e) => setNewClientData({...newClientData, name: e.target.value})}
                className="form-input"
                placeholder="Enter company name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Contact Email</label>
              <input
                type="email"
                value={newClientData.contact_email}
                onChange={(e) => setNewClientData({...newClientData, contact_email: e.target.value})}
                className="form-input"
                placeholder="email@example.com"
              />
            </div>

            <div className="form-group-lg">
              <label className="form-label">Contact Phone</label>
              <input
                type="tel"
                value={newClientData.contact_phone}
                onChange={(e) => setNewClientData({...newClientData, contact_phone: e.target.value})}
                className="form-input"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Account Manager</label>
              <select
                value={newClientData.account_manager_id || ''}
                onChange={(e) => setNewClientData({...newClientData, account_manager_id: e.target.value})}
                className="form-input"
              >
                <option value="">Select an account manager</option>
                <option value="11">Mike McKenna</option>
                <option value="12">Drew McKenna</option>
                <option value="14">Erin Lailer</option>
                <option value="15">Sally McCarthy</option>
                <option value="13">Cynde Smith</option>
              </select>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setShowNewClientModal(false)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={createNewClient}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#0d6efd',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={!newClientData.name}
              >
                Create Client
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Campaign Modal */}
      {showNewCampaignModal && (
        <div className={showMoveProjectModal ? "modal-backdrop-nested" : "modal-backdrop"}>
          <div className="modal-content">
            <h2 className="modal-title">Create New Campaign</h2>

            <div className="form-group">
              <label className="form-label">Client *</label>
              <select
                value={newCampaignData.client_id}
                onChange={(e) => setNewCampaignData({...newCampaignData, client_id: e.target.value})}
                className="form-input"
              >
                <option value="">Select a client</option>
                {dashboardData.clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Campaign Name *</label>
              <input
                type="text"
                value={newCampaignData.name}
                onChange={(e) => setNewCampaignData({...newCampaignData, name: e.target.value})}
                className="form-input"
                placeholder="Enter campaign name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={newCampaignData.description}
                onChange={(e) => setNewCampaignData({...newCampaignData, description: e.target.value})}
                className="form-textarea"
                placeholder="Campaign description"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Budget</label>
              <input
                type="number"
                value={newCampaignData.budget}
                onChange={(e) => setNewCampaignData({...newCampaignData, budget: e.target.value})}
                className="form-input"
                placeholder="50000"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                value={newCampaignData.start_date}
                onChange={(e) => setNewCampaignData({...newCampaignData, start_date: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group-lg">
              <label className="form-label">End Date</label>
              <input
                type="date"
                value={newCampaignData.end_date}
                onChange={(e) => setNewCampaignData({...newCampaignData, end_date: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Attach Files</label>
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  const validFiles = files.filter(f => f.size <= 10 * 1024 * 1024);
                  if (validFiles.length < files.length) {
                    showNotification('error', 'Some files exceed 10MB and were not added');
                  }
                  setCampaignFiles(prev => [...prev, ...validFiles]);
                }}
                className="form-input"
                style={{ padding: '8px' }}
              />
              {campaignFiles.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  {campaignFiles.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 10px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px',
                        marginBottom: '4px'
                      }}
                    >
                      <span style={{ fontSize: '13px', color: '#374151' }}>
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        onClick={() => setCampaignFiles(prev => prev.filter((_, i) => i !== index))}
                        style={{
                          padding: '2px 8px',
                          fontSize: '12px',
                          color: '#dc2626',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setShowNewCampaignModal(false)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={createNewCampaign}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#0d6efd',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={!newCampaignData.client_id || !newCampaignData.name}
              >
                Create Campaign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditClientModal && editingClient && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2 className="modal-title">Edit Client</h2>

            <div className="form-group">
              <label className="form-label">Company Name *</label>
              <input
                type="text"
                value={editingClient.name}
                onChange={(e) => setEditingClient({...editingClient, name: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Contact Name</label>
              <input
                type="text"
                value={editingClient.company || ''}
                onChange={(e) => setEditingClient({...editingClient, company: e.target.value})}
                className="form-input"
                placeholder="Primary contact person"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Contact Email</label>
              <input
                type="email"
                value={editingClient.contact_email || ''}
                onChange={(e) => setEditingClient({...editingClient, contact_email: e.target.value})}
                className="form-input"
                placeholder="contact@example.com"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Contact Phone</label>
              <input
                type="tel"
                value={editingClient.contact_phone || ''}
                onChange={(e) => setEditingClient({...editingClient, contact_phone: e.target.value})}
                className="form-input"
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Account Manager *</label>
              <select
                value={editingClient.account_manager_id || ''}
                onChange={(e) => setEditingClient({...editingClient, account_manager_id: parseInt(e.target.value) || undefined})}
                className="form-input"
              >
                <option value="">Select Account Manager</option>
                {accountManagers.map((manager: any) => (
                  <option key={manager.id} value={manager.id}>{manager.name}</option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowEditClientModal(false);
                  setEditingClient(null);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={updateClient}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#0d6efd',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {showEditCampaignModal && editingCampaign && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2 className="modal-title">Edit Campaign</h2>

            <div className="form-group">
              <label className="form-label">Campaign Name *</label>
              <input
                type="text"
                value={editingCampaign.name}
                onChange={(e) => setEditingCampaign({...editingCampaign, name: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={editingCampaign.description}
                onChange={(e) => setEditingCampaign({...editingCampaign, description: e.target.value})}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                value={editingCampaign.status}
                onChange={(e) => setEditingCampaign({...editingCampaign, status: e.target.value})}
                className="form-input"
              >
                <option value="Planning">Planning</option>
                <option value="Active">Active</option>
                <option value="On Hold">On Hold</option>
                <option value="Completed">Completed</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Progress %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={editingCampaign.progress_percentage}
                onChange={(e) => setEditingCampaign({...editingCampaign, progress_percentage: parseInt(e.target.value) || 0})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Budget</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={editingCampaign.budget || 0}
                onChange={(e) => setEditingCampaign({...editingCampaign, budget: parseFloat(e.target.value) || 0})}
                className="form-input"
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                value={editingCampaign.start_date || ''}
                onChange={(e) => setEditingCampaign({...editingCampaign, start_date: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="date"
                value={editingCampaign.end_date || ''}
                onChange={(e) => setEditingCampaign({...editingCampaign, end_date: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Account Manager</label>
              <select
                value={editingCampaign.account_manager_id || ''}
                onChange={(e) => setEditingCampaign({...editingCampaign, account_manager_id: parseInt(e.target.value) || undefined})}
                className="form-input"
              >
                <option value="">Select Account Manager</option>
                {teamMembers
                  .filter((member: any) => member.role_name === 'Account Manager')
                  .map((member: any) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowEditCampaignModal(false);
                  setEditingCampaign(null);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={updateCampaign}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#0d6efd',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditProjectModal && editingProject && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2 className="modal-title">Edit Project</h2>

            <div className="form-group">
              <label className="form-label">Project Name *</label>
              <input
                type="text"
                value={editingProject.name}
                onChange={(e) => setEditingProject({...editingProject, name: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={editingProject.description}
                onChange={(e) => setEditingProject({...editingProject, description: e.target.value})}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Account Manager</label>
              <select
                value={editingProject.account_manager_id || ''}
                onChange={(e) => setEditingProject({...editingProject, account_manager_id: parseInt(e.target.value) || undefined})}
                className="form-input"
              >
                <option value="">Select Account Manager</option>
                {accountManagers.map((manager: any) => (
                  <option key={manager.id} value={manager.id}>{manager.name}</option>
                ))}
              </select>
            </div>

            {editingProject.project_type === 'Voyager' && (
              <div className="form-group">
                <label className="form-label">Client URL</label>
                <input
                  type="url"
                  value={editingProject.client_url || ''}
                  onChange={(e) => setEditingProject({...editingProject, client_url: e.target.value})}
                  className="form-input"
                  placeholder="https://example.com"
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  value={editingProject.priority}
                  onChange={(e) => setEditingProject({...editingProject, priority: e.target.value})}
                  className="form-input"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  value={editingProject.status}
                  onChange={(e) => setEditingProject({...editingProject, status: e.target.value})}
                  className="form-input"
                >
                  <option value="Not Started">Not Started</option>
                  <option value="In Progress">In Progress</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowEditProjectModal(false);
                  setEditingProject(null);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={updateProject}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#0d6efd',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditTaskModal && editingTask && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <h2 className="modal-title">Edit Task</h2>

            <div className="form-group">
              <label className="form-label">Task Name *</label>
              <input
                type="text"
                value={editingTask.name}
                onChange={(e) => setEditingTask({...editingTask, name: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={editingTask.description}
                onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                className="form-textarea"
                rows={3}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  value={editingTask.status}
                  onChange={(e) => setEditingTask({...editingTask, status: e.target.value})}
                  className="form-input"
                >
                  <option value="Not Started">Not Started</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Ready for Review">Ready for Review</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  value={editingTask.priority}
                  onChange={(e) => setEditingTask({...editingTask, priority: e.target.value})}
                  className="form-input"
                >
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Phase</label>
                <select
                  value={editingTask.phase || ''}
                  onChange={(e) => setEditingTask({...editingTask, phase: e.target.value})}
                  className="form-input"
                >
                  <option value="">Select Phase</option>
                  <optgroup label="Strategy">
                    <option value="Strategy - Planning">Planning</option>
                    <option value="Strategy - Content">Content</option>
                  </optgroup>
                  <optgroup label="Fulfillment">
                    <option value="Fulfillment - Design">Design</option>
                    <option value="Fulfillment - Front End">Front End</option>
                    <option value="Fulfillment - Back End">Back End</option>
                    <option value="Fulfillment - Production">Production</option>
                    <option value="Fulfillment - Deploy">Deploy</option>
                  </optgroup>
                  <option value="Insights & Innovation">Insights & Innovation</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Assigned To</label>
                <select
                  value={editingTask.assigned_to || ''}
                  onChange={(e) => setEditingTask({...editingTask, assigned_to: e.target.value})}
                  className="form-input"
                >
                  <option value="">Select team member</option>
                  {teamMembers.map((member: any) => (
                    <option key={member.id} value={member.name}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  value={editingTask.due_date || ''}
                  onChange={(e) => setEditingTask({...editingTask, due_date: e.target.value})}
                  className="form-input"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Estimated Hours</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={editingTask.estimated_hours || 0}
                  onChange={(e) => setEditingTask({...editingTask, estimated_hours: parseFloat(e.target.value) || 0})}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Actual Hours</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={editingTask.actual_hours || 0}
                  onChange={(e) => setEditingTask({...editingTask, actual_hours: parseFloat(e.target.value) || 0})}
                  className="form-input"
                />
              </div>
            </div>

            <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
              <button
                onClick={() => {
                  if (editingTask && window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
                    confirmDelete('task', editingTask.id);
                    setShowEditTaskModal(false);
                    setEditingTask(null);
                  }
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#dc2626',
                  backgroundColor: 'white',
                  border: '1px solid #dc2626',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.color = '#dc2626';
                }}
              >
                Delete Task
              </button>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setShowEditTaskModal(false);
                    setEditingTask(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={updateTask}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'white',
                    backgroundColor: '#0d6efd',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2 className="modal-title">Create New Project</h2>

            {/* Mode Selection */}
            <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setProjectCreationMode('quick')}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: projectCreationMode === 'quick' ? 'white' : '#374151',
                  backgroundColor: projectCreationMode === 'quick' ? '#10b981' : 'white',
                  border: `2px solid ${projectCreationMode === 'quick' ? '#10b981' : '#d1d5db'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Quick Project
              </button>
              <button
                onClick={() => setProjectCreationMode('advanced')}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: projectCreationMode === 'advanced' ? 'white' : '#374151',
                  backgroundColor: projectCreationMode === 'advanced' ? '#3b82f6' : 'white',
                  border: `2px solid ${projectCreationMode === 'advanced' ? '#3b82f6' : '#d1d5db'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Advanced Project
              </button>
            </div>

            {/* Advanced Project Type Selection */}
            {projectCreationMode === 'advanced' && (
              <div className="form-group">
                <label className="form-label">Project Type *</label>
                <select
                  value={selectedProjectType}
                  onChange={(e) => setSelectedProjectType(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select a project type</option>
                  <option value="Google Ad">Google Ad</option>
                  <option value="Print Design">Print Design</option>
                  <option value="Publication">Publication</option>
                  <option value="SEO">SEO</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Video">Video</option>
                  <option value="Voyager">Voyager</option>
                  <option value="Website">Website</option>
                </select>
              </div>
            )}

            {projectCreationMode === 'advanced' && selectedProjectType === 'Voyager' && (
              <div className="form-group">
                <label className="form-label">Voyager Level *</label>
                <select
                  value={voyagerLevel}
                  onChange={(e) => setVoyagerLevel(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select a level</option>
                  <option value="Beacon">Beacon</option>
                  <option value="Compass">Compass</option>
                  <option value="Navigator">Navigator</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Select Campaign *</label>
              <select
                value={newProjectData.campaign_id}
                onChange={(e) => setNewProjectData({...newProjectData, campaign_id: e.target.value})}
                className="form-input"
              >
                <option value="">Select a campaign</option>
                {(selectedCampaign
                  ? allCampaigns.filter(c => c.client_id === selectedCampaign.client_id)
                  : allCampaigns
                ).map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name} ({campaign.client_name || 'Unknown Client'})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Project Name *</label>
              <input
                type="text"
                value={newProjectData.name}
                onChange={(e) => setNewProjectData({...newProjectData, name: e.target.value})}
                className="form-input"
                placeholder="Enter project name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={newProjectData.description}
                onChange={(e) => setNewProjectData({...newProjectData, description: e.target.value})}
                className="form-textarea"
                placeholder="Project description"
              />
            </div>

            <div className="form-group-lg">
              <label className="form-label">Priority</label>
              <select
                value={newProjectData.priority}
                onChange={(e) => setNewProjectData({...newProjectData, priority: e.target.value})}
                className="form-input"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Attach Files</label>
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  const validFiles = files.filter(f => f.size <= 10 * 1024 * 1024);
                  if (validFiles.length < files.length) {
                    showNotification('error', 'Some files exceed 10MB and were not added');
                  }
                  setProjectFiles(prev => [...prev, ...validFiles]);
                }}
                className="form-input"
                style={{ padding: '8px' }}
              />
              {projectFiles.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  {projectFiles.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 10px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px',
                        marginBottom: '4px'
                      }}
                    >
                      <span style={{ fontSize: '13px', color: '#374151' }}>
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        onClick={() => setProjectFiles(prev => prev.filter((_, i) => i !== index))}
                        style={{
                          padding: '2px 8px',
                          fontSize: '12px',
                          color: '#dc2626',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowNewProjectModal(false);
                  setProjectCreationMode('quick');
                  setSelectedProjectType('');
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={createNewProject}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#10b981',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={
                  !newProjectData.campaign_id ||
                  !newProjectData.name ||
                  (projectCreationMode === 'advanced' && !selectedProjectType) ||
                  (projectCreationMode === 'advanced' && selectedProjectType === 'Voyager' && !voyagerLevel)
                }
              >
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client/Campaign Selection Modal */}
      {showClientCampaignSelectModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <h2 className="modal-title">Select Client & Campaign</h2>

            {/* Client Selection */}
            <div className="form-group">
              <label className="form-label">Client *</label>
              {!showNewClientInline ? (
                <div>
                  <select
                    value={selectedClientForProject || ''}
                    onChange={(e) => {
                      setSelectedClientForProject(e.target.value ? parseInt(e.target.value) : null);
                      setSelectedCampaignForProject(null);
                    }}
                    className="form-input"
                  >
                    <option value="">Select a client</option>
                    {dashboardData.clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => setShowNewClientInline(true)}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#3b82f6',
                      backgroundColor: 'white',
                      border: '1px solid #3b82f6',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    + Add New Client
                  </button>
                </div>
              ) : (
                <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label className="form-label">Client Name *</label>
                    <input
                      type="text"
                      value={newClientData.name}
                      onChange={(e) => setNewClientData({...newClientData, name: e.target.value})}
                      className="form-input"
                      placeholder="Enter client name"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={async () => {
                        if (!newClientData.name) return;
                        try {
                          const response = await axios.post(`${API_URL}/api/clients`, newClientData);
                          await loadDashboardData();
                          setSelectedClientForProject(response.data.id);
                          setShowNewClientInline(false);
                          setNewClientData({ name: '', company: '', contact_email: '', contact_phone: '', account_manager_id: '' });
                          showNotification('success', 'Client created successfully!');
                        } catch (error) {
                          console.error('Failed to create client:', error);
                          showNotification('error', 'Failed to create client');
                        }
                      }}
                      disabled={!newClientData.name}
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: 'white',
                        backgroundColor: newClientData.name ? '#10b981' : '#d1d5db',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: newClientData.name ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Create Client
                    </button>
                    <button
                      onClick={() => {
                        setShowNewClientInline(false);
                        setNewClientData({ name: '', company: '', contact_email: '', contact_phone: '', account_manager_id: '' });
                      }}
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#374151',
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Campaign Selection */}
            {selectedClientForProject && !showNewClientInline && (
              <div className="form-group">
                <label className="form-label">Campaign *</label>
                {!showNewCampaignInline ? (
                  <div>
                    <select
                      value={selectedCampaignForProject || ''}
                      onChange={(e) => setSelectedCampaignForProject(e.target.value ? parseInt(e.target.value) : null)}
                      className="form-input"
                    >
                      <option value="">Select a campaign</option>
                      {allCampaigns
                        .filter((c) => c.client_id === selectedClientForProject)
                        .map((campaign) => (
                          <option key={campaign.id} value={campaign.id}>
                            {campaign.name}
                          </option>
                        ))}
                    </select>
                    <button
                      onClick={() => setShowNewCampaignInline(true)}
                      style={{
                        marginTop: '8px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#3b82f6',
                        backgroundColor: 'white',
                        border: '1px solid #3b82f6',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      + Add New Campaign
                    </button>
                  </div>
                ) : (
                  <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                    <div className="form-group" style={{ marginBottom: '12px' }}>
                      <label className="form-label">Campaign Name *</label>
                      <input
                        type="text"
                        value={newCampaignData.name}
                        onChange={(e) => setNewCampaignData({...newCampaignData, name: e.target.value})}
                        className="form-input"
                        placeholder="Enter campaign name"
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={async () => {
                          if (!newCampaignData.name || !selectedClientForProject) return;
                          try {
                            const campaignPayload = {
                              ...newCampaignData,
                              client_id: selectedClientForProject.toString(),
                              account_manager_id: selectedAccountManagerForPriority?.toString() || ''
                            };
                            const response = await axios.post(`${API_URL}/api/campaigns`, campaignPayload);
                            await loadDashboardData();
                            await loadAllCampaigns();
                            setSelectedCampaignForProject(response.data.id);
                            setShowNewCampaignInline(false);
                            setNewCampaignData({ client_id: '', name: '', description: '', budget: '', start_date: '', end_date: '', account_manager_id: '' });
                            showNotification('success', 'Campaign created successfully!');
                          } catch (error) {
                            console.error('Failed to create campaign:', error);
                            showNotification('error', 'Failed to create campaign');
                          }
                        }}
                        disabled={!newCampaignData.name}
                        style={{
                          padding: '6px 12px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: 'white',
                          backgroundColor: newCampaignData.name ? '#10b981' : '#d1d5db',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: newCampaignData.name ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Create Campaign
                      </button>
                      <button
                        onClick={() => {
                          setShowNewCampaignInline(false);
                          setNewCampaignData({ client_id: '', name: '', description: '', budget: '', start_date: '', end_date: '', account_manager_id: '' });
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#374151',
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowClientCampaignSelectModal(false);
                  setSelectedClientForProject(null);
                  setSelectedCampaignForProject(null);
                  setShowNewClientInline(false);
                  setShowNewCampaignInline(false);
                  setNewClientData({ name: '', company: '', contact_email: '', contact_phone: '', account_manager_id: '' });
                  setNewCampaignData({ client_id: '', name: '', description: '', budget: '', start_date: '', end_date: '', account_manager_id: '' });
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleProceedToProjectCreation}
                disabled={!selectedCampaignForProject || showNewClientInline || showNewCampaignInline}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: selectedCampaignForProject && !showNewClientInline && !showNewCampaignInline ? '#0d6efd' : '#d1d5db',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: selectedCampaignForProject && !showNewClientInline && !showNewCampaignInline ? 'pointer' : 'not-allowed'
                }}
              >
                Next: Create Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Project Modal */}
      {showMoveProjectModal && projectToMove && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2 className="modal-title">Move Project</h2>

            <div className="form-group">
              <label className="form-label">Project</label>
              <div style={{
                padding: '10px',
                backgroundColor: '#f3f4f6',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#374151'
              }}>
                {projectToMove.name}
              </div>
            </div>

            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label className="form-label" style={{ marginBottom: 0 }}>Select Client *</label>
                <button
                  onClick={() => {
                    setShowNewClientModal(true);
                  }}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  + Create Client
                </button>
              </div>
              <select
                value={moveProjectData.client_id}
                onChange={async (e) => {
                  const clientId = e.target.value;
                  setMoveProjectData({
                    ...moveProjectData,
                    client_id: clientId,
                    campaign_id: '' // Reset campaign when client changes
                  });
                  // Load campaigns for selected client
                  if (clientId) {
                    await loadCampaignsForClient(parseInt(clientId));
                  }
                }}
                className="form-input"
              >
                <option value="">Select a client</option>
                {dashboardData.clients
                  .filter((c: any) => c.id !== 999) // Exclude "Ongoing Projects"
                  .map((client: any) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
              </select>
            </div>

            {moveProjectData.client_id && (
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="form-label" style={{ marginBottom: 0 }}>Select Campaign *</label>
                  <button
                    onClick={() => {
                      setNewCampaignData({ ...newCampaignData, client_id: moveProjectData.client_id });
                      setShowNewCampaignModal(true);
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    + Create Campaign
                  </button>
                </div>
                <select
                  value={moveProjectData.campaign_id}
                  onChange={(e) => setMoveProjectData({...moveProjectData, campaign_id: e.target.value})}
                  className="form-input"
                >
                  <option value="">Select a campaign</option>
                  {campaigns
                    .filter((c: any) => c.client_id === parseInt(moveProjectData.client_id))
                    .map((campaign: any) => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowMoveProjectModal(false);
                  setProjectToMove(null);
                  setMoveProjectData({ client_id: '', campaign_id: '' });
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!moveProjectData.campaign_id || !projectToMove) return;

                  try {
                    await axios.put(
                      `${API_URL}/api/projects/${projectToMove.id}`,
                      { campaign_id: parseInt(moveProjectData.campaign_id) }
                    );

                    // Reload projects
                    await loadAllProjects();

                    // Close modal
                    setShowMoveProjectModal(false);
                    setProjectToMove(null);
                    setMoveProjectData({ client_id: '', campaign_id: '' });

                    // alert('Project moved successfully!');
                  } catch (error) {
                    console.error('Error moving project:', error);
                    alert('Failed to move project');
                  }
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={!moveProjectData.campaign_id}
              >
                Move Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Edit Project Modal */}
      {showQuickEditModal && quickEditProject && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <h2 className="modal-title">Edit Project</h2>

            <div className="form-group">
              <label className="form-label">Project Name *</label>
              <input
                type="text"
                value={quickEditProject.name}
                onChange={(e) => setQuickEditProject({...quickEditProject, name: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={quickEditProject.description || ''}
                onChange={(e) => setQuickEditProject({...quickEditProject, description: e.target.value})}
                className="form-input"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status *</label>
              <select
                value={quickEditProject.status}
                onChange={(e) => setQuickEditProject({...quickEditProject, status: e.target.value})}
                className="form-input"
              >
                <option value="Not Started">Not Started</option>
                <option value="In Progress">In Progress</option>
                <option value="On Hold">On Hold</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Phase</label>
              <select
                value={quickEditProject.phase || ''}
                onChange={(e) => setQuickEditProject({...quickEditProject, phase: e.target.value})}
                className="form-input"
              >
                <option value="">Select Phase</option>
                <optgroup label="Strategy">
                  <option value="Strategy - Planning">Planning</option>
                  <option value="Strategy - Content">Content</option>
                </optgroup>
                <optgroup label="Fulfillment">
                  <option value="Fulfillment - Design">Design</option>
                  <option value="Fulfillment - Front End">Front End</option>
                  <option value="Fulfillment - Back End">Back End</option>
                  <option value="Fulfillment - Production">Production</option>
                  <option value="Fulfillment - Deploy">Deploy</option>
                </optgroup>
                <option value="Insights & Innovation">Insights & Innovation</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Priority</label>
              <select
                value={quickEditProject.priority || 'Medium'}
                onChange={(e) => setQuickEditProject({...quickEditProject, priority: e.target.value})}
                className="form-input"
              >
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => {
                  // Close Edit Project modal and open Add Task modal
                  setNewTaskData({
                    ...newTaskData,
                    project_id: quickEditProject.id.toString(),
                    phase: quickEditProject.phase || 'Strategy',
                    priority: quickEditProject.priority || 'Medium'
                  });
                  setShowQuickEditModal(false);
                  setShowNewTaskModal(true);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                + Add Task
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    setShowQuickEditModal(false);
                    setQuickEditProject(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!quickEditProject.name) return;

                    try {
                      await axios.put(
                        `${API_URL}/api/projects/${quickEditProject.id}`,
                        {
                          name: quickEditProject.name,
                          description: quickEditProject.description,
                          status: quickEditProject.status,
                          phase: quickEditProject.phase,
                          priority: quickEditProject.priority
                        }
                      );

                      // Reload projects
                      await loadAllProjects();

                      // Close modal
                      setShowQuickEditModal(false);
                      setQuickEditProject(null);

                      // alert('Project updated successfully!');
                    } catch (error) {
                      console.error('Error updating project:', error);
                      alert('Failed to update project');
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  disabled={!quickEditProject.name}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Task Modal */}
      {showNewTaskModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2 className="modal-title">Create New Task</h2>

            {/* Only show project dropdown if project_id is not pre-filled */}
            {!newTaskData.project_id ? (
              <div className="form-group">
                <label className="form-label">Select Project *</label>
                <select
                  value={newTaskData.project_id}
                  onChange={(e) => setNewTaskData({...newTaskData, project_id: e.target.value})}
                  className="form-input"
                >
                  <option value="">Select a project</option>
                  {(selectedCampaign
                    ? projects.filter(p => p.campaign_id === selectedCampaign.id)
                    : projects
                  ).map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name} ({project.client_name})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Project</label>
                <div style={{
                  padding: '10px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#374151',
                  fontWeight: '500'
                }}>
                  {projects.find(p => p.id === parseInt(newTaskData.project_id))?.name || 'Unknown Project'}
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Task Name *</label>
              <input
                type="text"
                value={newTaskData.name}
                onChange={(e) => setNewTaskData({...newTaskData, name: e.target.value})}
                className="form-input"
                placeholder="Enter task name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={newTaskData.description}
                onChange={(e) => setNewTaskData({...newTaskData, description: e.target.value})}
                className="form-textarea"
                placeholder="Task description"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Assigned To *</label>
              <select
                value={newTaskData.assigned_to}
                onChange={(e) => setNewTaskData({...newTaskData, assigned_to: e.target.value})}
                className="form-input"
              >
                <option value="">Select team member</option>
                {teamMembers.map((member: any) => (
                  <option key={member.id} value={member.name}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input
                type="date"
                value={newTaskData.due_date}
                onChange={(e) => setNewTaskData({...newTaskData, due_date: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Priority</label>
              <select
                value={newTaskData.priority}
                onChange={(e) => setNewTaskData({...newTaskData, priority: e.target.value})}
                className="form-input"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Phase</label>
              <select
                value={newTaskData.phase || ''}
                onChange={(e) => setNewTaskData({...newTaskData, phase: e.target.value})}
                className="form-input"
              >
                <option value="">Select Phase</option>
                <optgroup label="Strategy">
                  <option value="Strategy - Planning">Planning</option>
                  <option value="Strategy - Content">Content</option>
                </optgroup>
                <optgroup label="Fulfillment">
                  <option value="Fulfillment - Design">Design</option>
                  <option value="Fulfillment - Front End">Front End</option>
                  <option value="Fulfillment - Back End">Back End</option>
                  <option value="Fulfillment - Production">Production</option>
                  <option value="Fulfillment - Deploy">Deploy</option>
                </optgroup>
                <option value="Insights & Innovation">Insights & Innovation</option>
              </select>
            </div>

            <div className="form-group-lg">
              <label className="form-label">Estimated Hours</label>
              <input
                type="number"
                min="0"
                value={newTaskData.estimated_hours}
                onChange={(e) => setNewTaskData({...newTaskData, estimated_hours: e.target.value})}
                className="form-input"
                placeholder="8"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Attach Files</label>
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  const validFiles = files.filter(f => f.size <= 10 * 1024 * 1024);
                  if (validFiles.length < files.length) {
                    showNotification('error', 'Some files exceed 10MB and were not added');
                  }
                  setTaskFiles(prev => [...prev, ...validFiles]);
                }}
                className="form-input"
                style={{ padding: '8px' }}
              />
              {taskFiles.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  {taskFiles.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 10px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '4px',
                        marginBottom: '4px'
                      }}
                    >
                      <span style={{ fontSize: '13px', color: '#374151' }}>
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        onClick={() => setTaskFiles(prev => prev.filter((_, i) => i !== index))}
                        style={{
                          padding: '2px 8px',
                          fontSize: '12px',
                          color: '#dc2626',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setShowNewTaskModal(false)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={createNewTask}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#0d6efd',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={!newTaskData.project_id || !newTaskData.name || !newTaskData.assigned_to}
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Priority Modal */}
      {showNewPriorityModal && selectedAccountManagerForPriority && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2 className="modal-title">Create New Priority</h2>

            <div className="form-group">
              <label className="form-label">Title *</label>
              <input
                type="text"
                value={newPriorityData.title}
                onChange={(e) => setNewPriorityData({...newPriorityData, title: e.target.value})}
                className="form-input"
                placeholder="Enter priority title"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={newPriorityData.description}
                onChange={(e) => setNewPriorityData({...newPriorityData, description: e.target.value})}
                className="form-textarea"
                placeholder="Priority description"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input
                type="date"
                value={newPriorityData.due_date}
                onChange={(e) => setNewPriorityData({...newPriorityData, due_date: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Priority</label>
              <select
                value={newPriorityData.priority}
                onChange={(e) => setNewPriorityData({...newPriorityData, priority: e.target.value})}
                className="form-input"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                value={newPriorityData.status}
                onChange={(e) => setNewPriorityData({...newPriorityData, status: e.target.value})}
                className="form-input"
              >
                <option value="Not Started">Not Started</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Blocked">Blocked</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Resource</label>
              <input
                type="text"
                value={newPriorityData.resource}
                onChange={(e) => setNewPriorityData({...newPriorityData, resource: e.target.value})}
                className="form-input"
                placeholder="e.g., Jesse Rykerson"
              />
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowNewPriorityModal(false);
                  setSelectedAccountManagerForPriority(null);
                  setNewPriorityData({ title: '', description: '', due_date: '', priority: 'Medium', status: 'Not Started', resource: '' });
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={createNewPriority}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#0d6efd',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={!newPriorityData.title}
              >
                Create Priority
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Priority Detail Modal */}
      {showPriorityDetailModal && selectedPriority && editingPriority && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
              <h2 className="modal-title">Priority Details</h2>
              <button
                onClick={() => {
                  setShowPriorityDetailModal(false);
                  setSelectedPriority(null);
                  setEditingPriority(null);
                }}
                style={{
                  padding: '8px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '20px',
                  color: '#6b7280'
                }}
              >
                ✕
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Title *</label>
              <input
                type="text"
                value={editingPriority.title}
                onChange={(e) => setEditingPriority({...editingPriority, title: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={editingPriority.description || ''}
                onChange={(e) => setEditingPriority({...editingPriority, description: e.target.value})}
                className="form-textarea"
                rows={4}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input
                type="date"
                value={editingPriority.due_date || ''}
                onChange={(e) => setEditingPriority({...editingPriority, due_date: e.target.value})}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Priority</label>
              <select
                value={editingPriority.priority}
                onChange={(e) => setEditingPriority({...editingPriority, priority: e.target.value})}
                className="form-input"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                value={editingPriority.status}
                onChange={(e) => setEditingPriority({...editingPriority, status: e.target.value})}
                className="form-input"
              >
                <option value="Not Started">Not Started</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Blocked">Blocked</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Resource</label>
              <input
                type="text"
                value={editingPriority.resource || ''}
                onChange={(e) => setEditingPriority({...editingPriority, resource: e.target.value})}
                className="form-input"
                placeholder="e.g., Jesse Rykerson"
              />
            </div>

            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => deletePriority(selectedPriority.id)}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#dc3545',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    setShowPriorityDetailModal(false);
                    setSelectedPriority(null);
                    setEditingPriority(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={updatePriority}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'white',
                    backgroundColor: '#0d6efd',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  disabled={!editingPriority.title}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {showTaskDetailModal && selectedTask && (
        <div className="modal-backdrop">
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            width: '480px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Task Details</h3>
              <button
                onClick={() => setShowTaskDetailModal(false)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#6b7280'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '8px' }}>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: 0, flex: 1, paddingRight: '8px' }}>
                  {selectedTask.name}
                </p>
                <span className={`px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap ${getPriorityColor(selectedTask.priority)}`}>
                  {selectedTask.priority}
                </span>
              </div>

              {selectedTask.description && (
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
                  {selectedTask.description}
                </p>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(selectedTask.status)}`}>
                  {selectedTask.status}
                </span>
                {selectedTask.due_date && (
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    Due: {formatDate(selectedTask.due_date)}
                  </span>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                <span style={{ fontWeight: '600', color: '#4b5563' }}>Assigned to:</span> {selectedTask.assigned_to}
              </p>
              {selectedTask.created_by && (
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '600', color: '#4b5563' }}>Created by:</span> {selectedTask.created_by}
                </p>
              )}
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                <span style={{ fontWeight: '600', color: '#4b5563' }}>Project:</span> {selectedTask.project_name}
              </p>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                <span style={{ fontWeight: '600', color: '#4b5563' }}>Campaign:</span> {selectedTask.campaign_name}
              </p>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                <span style={{ fontWeight: '600', color: '#4b5563' }}>Client:</span> {selectedTask.client_name}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '2px' }}>ESTIMATED</p>
                <p style={{ fontSize: '13px', color: '#111827', fontWeight: '600' }}>{selectedTask.estimated_hours}h</p>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '2px' }}>ACTUAL</p>
                <p style={{ fontSize: '13px', color: '#111827', fontWeight: '600' }}>{selectedTask.actual_hours || 0}h</p>
              </div>
            </div>

            {/* Comments Section */}
            <div style={{ marginBottom: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
                Communication ({taskComments.length})
              </h4>

              {/* Comments List */}
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px' }}>
                {taskComments.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>No comments yet</p>
                ) : (
                  taskComments.map((comment: any) => (
                    <div key={comment.id} style={{
                      backgroundColor: '#f9fafb',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                          {comment.user_name}
                        </span>
                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                          {new Date(comment.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div
                        style={{ fontSize: '13px', color: '#4b5563', margin: 0 }}
                        dangerouslySetInnerHTML={{ __html: comment.comment }}
                      />
                    </div>
                  ))
                )}
              </div>

              {/* Add Comment Form */}
              <div style={{ marginBottom: '12px' }}>
                <RichTextEditor
                  content={newComment}
                  onChange={setNewComment}
                  placeholder="Add a comment..."
                  onFileUpload={handleCommentFileUpload}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    if (newComment.trim() && selectedTask) {
                      addTaskComment(selectedTask.id, newComment);
                      setNewComment('');
                    }
                  }}
                  disabled={!newComment.trim()}
                  style={{
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: 'white',
                    backgroundColor: newComment.trim() ? '#0d6efd' : '#9ca3af',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: newComment.trim() ? 'pointer' : 'not-allowed',
                    alignSelf: 'flex-end'
                  }}
                >
                  Send
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => {
                  setEditingTask(selectedTask);
                  setShowTaskDetailModal(false);
                  setShowEditTaskModal(true);
                }}
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#0d6efd',
                  backgroundColor: 'white',
                  border: '1px solid #0d6efd',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Edit
              </button>
              <button
                onClick={() => setShowTaskDetailModal(false)}
                style={{
                  padding: '6px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="modal-backdrop">
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', width: '400px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#dc2626' }}>Confirm Delete</h2>

            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '24px' }}>
              Are you sure you want to delete this {deleteType}?
              {deleteType === 'client' && ' This will also delete all associated campaigns, projects, and tasks.'}
              {deleteType === 'campaign' && ' This will also delete all associated projects and tasks.'}
              {' '}This action cannot be undone.
            </p>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteType(null);
                  setDeleteId(null);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={executeDelete}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#dc2626',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Task Import Modal */}
      {showBulkTaskModal && (
        <div className="modal-overlay" onClick={() => setShowBulkTaskModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>Bulk Add Tasks</h2>
              <button
                className="modal-close"
                onClick={() => setShowBulkTaskModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* Instructions */}
              <div style={{
                backgroundColor: '#f3f4f6',
                padding: '12px 16px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#4b5563'
              }}>
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>How to use:</p>
                <ul style={{ marginLeft: '20px', lineHeight: '1.6' }}>
                  <li><strong>Simple format:</strong> One task per line
                    <div style={{ fontFamily: 'monospace', backgroundColor: '#e5e7eb', padding: '4px 8px', marginTop: '4px', borderRadius: '4px' }}>
                      Design homepage mockup<br/>
                      Review client feedback<br/>
                      Update brand guidelines
                    </div>
                  </li>
                  <li style={{ marginTop: '8px' }}><strong>Structured format:</strong> Use | to separate fields
                    <div style={{ fontFamily: 'monospace', backgroundColor: '#e5e7eb', padding: '4px 8px', marginTop: '4px', borderRadius: '4px' }}>
                      Task Name | Priority | Phase | Assignee | Due Date | Hours<br/>
                      Design homepage | High | Design | John | 2025-10-05 | 8<br/>
                      Review feedback | Medium | Content | Sarah | 2025-10-03 | 4
                    </div>
                  </li>
                </ul>
                <p style={{ marginTop: '8px', fontSize: '12px', fontStyle: 'italic' }}>
                  Defaults: Priority = Medium, Phase = Strategy, Assignee = Unassigned, Due Date = 7 days from now, Hours = 0
                </p>
              </div>

              {/* Text area */}
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                  Paste your tasks here:
                </label>
                <textarea
                  value={bulkTaskText}
                  onChange={(e) => handleBulkTaskTextChange(e.target.value)}
                  placeholder="Design homepage mockup&#10;Review client feedback | High | John | 2025-10-05 | 8&#10;Update brand guidelines"
                  style={{
                    width: '100%',
                    minHeight: '200px',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Preview */}
              {bulkTaskPreview.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                    Preview ({bulkTaskPreview.length} tasks):
                  </h3>
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '12px'
                  }}>
                    {bulkTaskPreview.map((task, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '10px',
                          marginBottom: '8px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      >
                        <div style={{ fontWeight: '600', marginBottom: '4px', color: '#111827' }}>
                          {index + 1}. {task.name}
                        </div>
                        <div style={{ display: 'flex', gap: '16px', color: '#6b7280', fontSize: '12px' }}>
                          <span>Priority: <strong>{task.priority}</strong></span>
                          <span>Phase: <strong>{task.phase}</strong></span>
                          <span>Assigned: <strong>{task.assigned_to}</strong></span>
                          <span>Due: <strong>{task.due_date}</strong></span>
                          <span>Hours: <strong>{task.estimated_hours}</strong></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowBulkTaskModal(false);
                  setBulkTaskText('');
                  setBulkTaskPreview([]);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkTaskImport}
                disabled={bulkTaskPreview.length === 0}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: bulkTaskPreview.length === 0 ? '#9ca3af' : '#10b981',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: bulkTaskPreview.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                Import {bulkTaskPreview.length} Tasks
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Management Modal */}
      {showUserModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2 className="modal-title">{editingUser ? 'Edit User' : 'Create New User'}</h2>

            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                value={userFormData.name}
                onChange={(e) => setUserFormData({...userFormData, name: e.target.value})}
                className="form-input"
                placeholder="Full name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({...userFormData, email: e.target.value})}
                className="form-input"
                placeholder="user@propagate.team"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Role *</label>
              <select
                value={userFormData.role_id}
                onChange={(e) => setUserFormData({...userFormData, role_id: e.target.value})}
                className="form-input"
              >
                <option value="">Select a role</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Slack User ID (for notifications)</label>
              <input
                type="text"
                value={userFormData.slack_user_id}
                onChange={(e) => setUserFormData({...userFormData, slack_user_id: e.target.value})}
                className="form-input"
                placeholder="e.g., U01234ABCD"
              />
              <p className="text-xs text-gray-500 mt-1">
                Find this in Slack: Right-click user → View profile → ⋯ (More) → Copy member ID
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">
                Password {editingUser ? '(leave blank to keep current)' : '*'}
              </label>
              <input
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({...userFormData, password: e.target.value})}
                className="form-input"
                placeholder={editingUser ? 'Leave blank to keep current' : 'Minimum 8 characters'}
              />
              {!editingUser && (
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank to use default password: "resetme" (user will be required to reset)
                </p>
              )}
            </div>

            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setEditingUser(null);
                  setUserFormData({ name: '', email: '', role_id: '', password: '', slack_user_id: '' });
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveUser}
                disabled={!userFormData.name || !userFormData.email || !userFormData.role_id}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: (!userFormData.name || !userFormData.email || !userFormData.role_id) ? '#9ca3af' : '#3b82f6',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (!userFormData.name || !userFormData.email || !userFormData.role_id) ? 'not-allowed' : 'pointer'
                }}
              >
                {editingUser ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Strategic Discovery Form */}
      {showDiscoveryForm && discoveryClientId && discoveryProjectType && (
        <StrategicDiscoveryForm
          clientId={discoveryClientId}
          projectType={discoveryProjectType}
          onClose={() => {
            setShowDiscoveryForm(false);
            setDiscoveryClientId(null);
            setDiscoveryProjectType('');
          }}
          onSave={() => {
            // Optionally refresh data or show success message
            console.log('Discovery form saved successfully');
          }}
        />
      )}
    </div>
  );
}

export default App;